import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { supabase } from "./supabase";

// ─── Config ───────────────────────────────────────────────────────────────────
const R2_WORKER = "https://r2-upload.gcs-workers.workers.dev";
const CLAUDE_MODEL = "claude-sonnet-4-20250514";

const COMPANIES = [
  { id:"synopsis",    name:"Synopsis-Tech",  domain:"synopsis-tech.net",  color:"#22d3ee", bg:"rgba(34,211,238,0.10)",  zohoAccount:"Synopsis-Tech Expenses"  },
  { id:"incloudites", name:"Incloudites",    domain:"incloudites.com",     color:"#a78bfa", bg:"rgba(167,139,250,0.10)", zohoAccount:"Incloudites Expenses"     },
];
const companyById = id => COMPANIES.find(c=>c.id===id)||COMPANIES[0];

const CURRENCIES = [
  {code:"USD",flag:"🇺🇸",name:"US Dollar"},{code:"EUR",flag:"🇪🇺",name:"Euro"},{code:"GBP",flag:"🇬🇧",name:"British Pound"},
  {code:"CHF",flag:"🇨🇭",name:"Swiss Franc"},{code:"NOK",flag:"🇳🇴",name:"Norwegian Krone"},{code:"SEK",flag:"🇸🇪",name:"Swedish Krona"},
  {code:"DKK",flag:"🇩🇰",name:"Danish Krone"},{code:"PLN",flag:"🇵🇱",name:"Polish Złoty"},{code:"TRY",flag:"🇹🇷",name:"Turkish Lira"},
  {code:"MAD",flag:"🇲🇦",name:"Moroccan Dirham"},{code:"TND",flag:"🇹🇳",name:"Tunisian Dinar"},{code:"DZD",flag:"🇩🇿",name:"Algerian Dinar"},
  {code:"EGP",flag:"🇪🇬",name:"Egyptian Pound"},{code:"SAR",flag:"🇸🇦",name:"Saudi Riyal"},{code:"AED",flag:"🇦🇪",name:"UAE Dirham"},
  {code:"QAR",flag:"🇶🇦",name:"Qatari Riyal"},{code:"KWD",flag:"🇰🇼",name:"Kuwaiti Dinar"},{code:"BHD",flag:"🇧🇭",name:"Bahraini Dinar"},
  {code:"OMR",flag:"🇴🇲",name:"Omani Rial"},{code:"JOD",flag:"🇯🇴",name:"Jordanian Dinar"},{code:"NGN",flag:"🇳🇬",name:"Nigerian Naira"},
  {code:"KES",flag:"🇰🇪",name:"Kenyan Shilling"},{code:"ZAR",flag:"🇿🇦",name:"South African Rand"},{code:"XOF",flag:"🌍",name:"West African CFA"},
];

const ROLES       = ["employee","manager1","manager2","admin"];
const DEPARTMENTS = ["Sales","Engineering","Finance","Operations","Marketing","HR","Management","Other"];
const PURPOSES    = ["Client Visit","Conference","Training","Internal Meeting","Sales Trip","Other"];

const mapSettings = s => ({
  company:s.company,
  approver1Id:s.approver1_id,
  approver2Id:s.approver2_id,
  approver3Id:s.approver3_id||null,
  approverCount:s.approver_count||2,
});

const STATUS_META = {
  draft:          {label:"Draft",       color:"#94a3b8",bg:"rgba(148,163,184,0.12)"},
  pending:        {label:"Pending",     color:"#f59e0b",bg:"rgba(245,158,11,0.12)"},
  approved_by_m1: {label:"Awaiting M2", color:"#6366f1",bg:"rgba(99,102,241,0.12)"},
  approved:       {label:"Approved",    color:"#10b981",bg:"rgba(16,185,129,0.12)"},
  rejected:       {label:"Rejected",    color:"#ef4444",bg:"rgba(239,68,68,0.12)"},
  flagged:        {label:"Needs Fix",   color:"#f97316",bg:"rgba(249,115,22,0.12)"},
  paid:           {label:"Paid",        color:"#06b6d4",bg:"rgba(6,182,212,0.12)"},
};

const fmt    = (n,d=2) => Number(n||0).toLocaleString("en-US",{minimumFractionDigits:d,maximumFractionDigits:d});
const fmtUSD = n => `$${fmt(n)}`;
const genId  = () => "u"+Date.now()+Math.random().toString(36).slice(2,6);
const genPwd = () => "Welcome@"+Math.floor(1000+Math.random()*9000);

// ─── Theme Context ────────────────────────────────────────────────────────────
const ThemeCtx = createContext("dark");
const useTheme = () => useContext(ThemeCtx);

const THEMES = {
  dark: {
    appBg:"#0b0e17", sidebarBg:"#111520", cardBg:"#111520", inputBg:"#161c2d",
    statBg:"#161c2d", text:"#e2e8f0", subText:"#475569", border:"#1a2035",
    topbar:"#111520", tickerBg:"#0b0e17", hoverBg:"rgba(255,255,255,0.015)",
    navColor:"#64748b", navActive:"#fff", navActiveBg:"rgba(34,211,238,0.08)",
    modalBg:"#111520", scrollTrack:"#0b0e17", scrollThumb:"#1a2035",
    selectOpt:"#161c2d",
  },
  light: {
    appBg:"#f1f5f9", sidebarBg:"#ffffff", cardBg:"#ffffff", inputBg:"#f8fafc",
    statBg:"#f8fafc", text:"#0f172a", subText:"#334155", border:"#e2e8f0",
    topbar:"#ffffff", tickerBg:"#f8fafc", hoverBg:"rgba(0,0,0,0.02)",
    navColor:"#1e293b", navActive:"#0f172a", navActiveBg:"rgba(34,211,238,0.10)",
    modalBg:"#ffffff", scrollTrack:"#f1f5f9", scrollThumb:"#e2e8f0",
    selectOpt:"#ffffff",
  },
};

// ─── DB Mappers ───────────────────────────────────────────────────────────────
const mapUser = u => ({
  id:u.id,name:u.name,email:u.email,password:u.password,role:u.role,
  company:u.company,phone:u.phone||"",department:u.department||"",
  active:u.active,mustChangePwd:u.must_change_pwd,invited:u.invited,
  authId:u.auth_id||null,
});
const userToDb = u => ({
  id:u.id,name:u.name,email:u.email,password:u.password,role:u.role,
  company:u.company,phone:u.phone,department:u.department,
  active:u.active,must_change_pwd:u.mustChangePwd,invited:u.invited,
  auth_id:u.authId||null,
});
const mapExp = e => ({
  id:e.id,employeeId:e.employee_id,employeeName:e.employee_name,company:e.company,
  category:e.category,amount:Number(e.amount),currency:e.currency,
  amountUSD:Number(e.amount_usd),rateUsed:Number(e.rate_used),
  date:e.date,description:e.description,vendor:e.vendor||"",status:e.status,
  m1ApprovedBy:e.m1_approved_by,m2ApprovedBy:e.m2_approved_by,
  m1Comment:e.m1_comment||"",m2Comment:e.m2_comment||"",
  pushedToZoho:e.pushed_to_zoho,paid:e.paid,createdAt:e.created_at,
  receiptUrl:e.receipt_url||null,tripId:e.trip_id||null,
  flagged:e.flagged||false,flagNote:e.flag_note||"",
  archived:e.archived||false,
  deletedAt:e.deleted_at||null,
});
const expToDb = e => ({
  id:e.id,employee_id:e.employeeId,employee_name:e.employeeName,company:e.company,
  category:e.category,amount:e.amount,currency:e.currency,
  amount_usd:e.amountUSD,rate_used:e.rateUsed,
  date:e.date,description:e.description,vendor:e.vendor,status:e.status,
  m1_approved_by:e.m1ApprovedBy,m2_approved_by:e.m2ApprovedBy,
  m1_comment:e.m1Comment,m2_comment:e.m2Comment,
  pushed_to_zoho:e.pushedToZoho,paid:e.paid,created_at:e.createdAt,
  receipt_url:e.receiptUrl||null,trip_id:e.tripId||null,
  flagged:e.flagged||false,flag_note:e.flagNote||null,
  archived:e.archived||false,
  deleted_at:e.deletedAt||null,
});
const mapTrip = t => ({
  id:t.id,name:t.name,destination:t.destination||"",purpose:t.purpose||"",
  startDate:t.start_date,endDate:t.end_date,budget:Number(t.budget||0),
  employeeId:t.employee_id,employeeName:t.employee_name,company:t.company,
  status:t.status,m1ApprovedBy:t.m1_approved_by,m2ApprovedBy:t.m2_approved_by,
  m1Comment:t.m1_comment||"",m2Comment:t.m2_comment||"",createdAt:t.created_at,
  deletedAt:t.deleted_at||null,
});
const tripToDb = t => ({
  id:t.id,name:t.name,destination:t.destination,purpose:t.purpose,
  start_date:t.startDate,end_date:t.endDate,budget:t.budget,
  employee_id:t.employeeId,employee_name:t.employeeName,company:t.company,
  status:t.status,m1_approved_by:t.m1ApprovedBy,m2_approved_by:t.m2ApprovedBy,
  m1_comment:t.m1Comment,m2_comment:t.m2Comment,created_at:t.createdAt,
  deleted_at:t.deletedAt||null,
});

const SEED_USERS = [
  {id:"u1",name:"Alice Martin",  email:"alice@synopsis-tech.net",  password:"pass123",role:"employee", company:"synopsis",    phone:"+212 600 001 001",department:"Sales",      active:true,mustChangePwd:false,invited:true},
  {id:"u2",name:"Bob Dupont",    email:"bob@incloudites.com",       password:"pass123",role:"employee", company:"incloudites", phone:"+212 600 002 002",department:"Engineering",active:true,mustChangePwd:false,invited:true},
  {id:"u3",name:"Carol Lemaire", email:"carol@synopsis-tech.net",  password:"pass123",role:"manager1", company:"synopsis",    phone:"+212 600 003 003",department:"Management", active:true,mustChangePwd:false,invited:true},
  {id:"u4",name:"David Moreau",  email:"david@incloudites.com",     password:"pass123",role:"manager2", company:"incloudites", phone:"+212 600 004 004",department:"Management", active:true,mustChangePwd:false,invited:true},
  {id:"u5",name:"Admin Finance", email:"admin@synopsis-tech.net",  password:"pass123",role:"admin",    company:"synopsis",    phone:"+212 600 005 005",department:"Finance",    active:true,mustChangePwd:false,invited:true},
];

// ─── Dynamic Styles ───────────────────────────────────────────────────────────
function useS() {
  const theme = useTheme();
  const t = THEMES[theme];
  return {
    app:       {fontFamily:"'DM Sans',sans-serif",display:"flex",height:"100vh",background:t.appBg,color:t.text,overflow:"hidden"},
    sidebar:   {width:240,background:t.sidebarBg,borderRight:`1px solid ${t.border}`,display:"flex",flexDirection:"column",padding:"24px 0",flexShrink:0},
    logo:      {padding:"0 24px 28px",borderBottom:`1px solid ${t.border}`,marginBottom:8},
    navItem:   {display:"flex",alignItems:"center",gap:10,padding:"10px 24px",cursor:"pointer",fontSize:13.5,fontWeight:500,color:t.navColor,border:"none",background:"none",width:"100%",textAlign:"left",transition:"all .15s"},
    navActive: {color:t.navActive,background:t.navActiveBg,borderLeft:"3px solid #22d3ee"},
    main:      {flex:1,display:"flex",flexDirection:"column",overflow:"hidden"},
    topbar:    {height:60,background:t.topbar,borderBottom:`1px solid ${t.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 28px",flexShrink:0},
    content:   {flex:1,overflow:"auto",padding:28},
    card:      {background:t.cardBg,border:`1px solid ${t.border}`,borderRadius:12,padding:24,marginBottom:20},
    h2:        {fontSize:20,fontWeight:800,color:t.text,margin:"0 0 4px",letterSpacing:"-0.5px"},
    sub:       {color:t.subText,fontSize:13,margin:"0 0 24px"},
    grid2:     {display:"grid",gridTemplateColumns:"1fr 1fr",gap:20},
    grid3:     {display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16},
    statCard:  {background:t.statBg,border:`1px solid ${t.border}`,borderRadius:12,padding:20},
    badge:     s=>({display:"inline-flex",alignItems:"center",padding:"3px 10px",borderRadius:20,fontSize:11.5,fontWeight:600,color:STATUS_META[s]?.color||"#94a3b8",background:STATUS_META[s]?.bg||"rgba(148,163,184,0.1)"}),
    btn:       {padding:"9px 18px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,transition:"all .15s"},
    btnPrimary:{background:"#22d3ee",color:"#0b0e17"},
    btnGhost:  {background:theme==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)",color:t.subText},
    btnDanger: {background:"rgba(239,68,68,0.12)",color:"#ef4444",border:"1px solid rgba(239,68,68,0.25)"},
    btnSuccess:{background:"rgba(16,185,129,0.12)",color:"#10b981",border:"1px solid rgba(16,185,129,0.25)"},
    btnWarn:   {background:"rgba(249,115,22,0.12)",color:"#f97316",border:"1px solid rgba(249,115,22,0.25)"},
    btnZoho:   {background:"rgba(99,102,241,0.12)",color:"#818cf8",border:"1px solid rgba(99,102,241,0.25)"},
    input:     {background:t.inputBg,border:`1px solid ${t.border}`,borderRadius:8,padding:"10px 14px",color:t.text,fontSize:13.5,width:"100%",outline:"none",boxSizing:"border-box"},
    label:     {fontSize:11.5,fontWeight:700,color:t.subText,marginBottom:6,display:"block",textTransform:"uppercase",letterSpacing:".6px"},
    table:     {width:"100%",borderCollapse:"collapse"},
    th:        {textAlign:"left",padding:"10px 14px",fontSize:11,fontWeight:700,color:t.subText,textTransform:"uppercase",letterSpacing:".5px",borderBottom:`1px solid ${t.border}`},
    td:        {padding:"12px 14px",fontSize:13.5,borderBottom:`1px solid ${t.appBg}`,verticalAlign:"middle"},
    modal:     {position:"fixed",inset:0,background:"rgba(0,0,0,.78)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100},
    modalBox:  {background:t.modalBg,border:`1px solid ${t.border}`,borderRadius:16,padding:32,width:600,maxHeight:"92vh",overflowY:"auto"},
    modalSm:   {background:t.modalBg,border:`1px solid ${t.border}`,borderRadius:16,padding:32,width:460},
    t, theme,
  };
}

// ─── UI Helpers ───────────────────────────────────────────────────────────────
function CompanyTag({companyId,size="sm"}) {
  const c=companyById(companyId);
  return <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:size==="lg"?"5px 14px":"2px 9px",borderRadius:20,fontSize:size==="lg"?13:11.5,fontWeight:700,color:c.color,background:c.bg,border:`1px solid ${c.color}30`,whiteSpace:"nowrap"}}>{c.name}</span>;
}
function Badge({status}) {
  const meta=STATUS_META[status]||{label:status,color:"#94a3b8",bg:"rgba(148,163,184,0.1)"};
  return <span style={{display:"inline-flex",alignItems:"center",padding:"3px 10px",borderRadius:20,fontSize:11.5,fontWeight:600,color:meta.color,background:meta.bg}}>{meta.label}</span>;
}
function RolePill({role}) {
  const colors={admin:"#f59e0b",manager1:"#a78bfa",manager2:"#6366f1",employee:"#64748b"};
  return <span style={{display:"inline-flex",padding:"2px 9px",borderRadius:20,fontSize:11.5,fontWeight:700,color:colors[role]||"#64748b",background:`${colors[role]||"#64748b"}18`}}>{role}</span>;
}
function Avatar({name,size=32}) {
  const initials=name.split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase();
  const cols=["#22d3ee","#a78bfa","#10b981","#f59e0b","#f472b6","#6366f1"];
  const col=cols[name.charCodeAt(0)%cols.length];
  return <div style={{width:size,height:size,borderRadius:"50%",background:`${col}25`,border:`1.5px solid ${col}50`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.35,fontWeight:800,color:col,flexShrink:0}}>{initials}</div>;
}

// ─── Live Rates ───────────────────────────────────────────────────────────────
function useRates() {
  const [rates,setRates]=useState({USD:1}); const [loading,setLoading]=useState(true); const [updated,setUpdated]=useState(null);
  useEffect(()=>{
    fetch("https://api.frankfurter.dev/v1/latest?from=USD").then(r=>r.json()).then(d=>{setRates({USD:1,...d.rates});setUpdated(d.date);}).catch(()=>{setRates({USD:1,EUR:0.92,GBP:0.79,CHF:0.89,NOK:10.6,SEK:10.4,DKK:6.88,PLN:4.02,TRY:32.1,MAD:10.1,TND:3.12,DZD:134.5,EGP:48.9,SAR:3.75,AED:3.67,QAR:3.64,KWD:0.307,BHD:0.376,OMR:0.385,JOD:0.709,NGN:1550,KES:129,ZAR:18.7,XOF:603});setUpdated("offline");}).finally(()=>setLoading(false));
  },[]);
  const toUSD=useCallback((amount,currency)=>{if(currency==="USD")return amount;const r=rates[currency];return r?amount/r:amount;},[rates]);
  return {rates,toUSD,loading,updated};
}

function RatesTicker({rates,updated}) {
  const S=useS(); const t=S.t;
  const show=["EUR","GBP","MAD","AED","SAR","TND","EGP","NGN","ZAR","TRY","CHF","KWD"];
  return (
    <div style={{background:t.tickerBg,borderBottom:`1px solid ${t.border}`,padding:"6px 28px",display:"flex",gap:24,overflowX:"auto",alignItems:"center",flexShrink:0}}>
      <span style={{fontSize:10,color:t.subText,fontWeight:700,whiteSpace:"nowrap",flexShrink:0}}>1 USD =</span>
      {show.map(c=>rates[c]?(<span key={c} style={{fontSize:11.5,color:t.subText,whiteSpace:"nowrap",flexShrink:0}}><span style={{color:"#22d3ee",fontWeight:700}}>{fmt(rates[c],3)}</span> {c}</span>):null)}
      {updated&&<span style={{fontSize:10,color:t.subText,opacity:.4,marginLeft:"auto",flexShrink:0,whiteSpace:"nowrap"}}>{updated==="offline"?"⚠ offline rates":`live · ${updated}`}</span>}
    </div>
  );
}

// ─── PDF Export ───────────────────────────────────────────────────────────────
function exportTripPDF(trip, tripExps, users) {
  const a1 = users.find(u=>u.id===trip.m1ApprovedBy)?.name||"—";
  const a2 = users.find(u=>u.id===trip.m2ApprovedBy)?.name||"—";
  const total = tripExps.reduce((s,e)=>s+(e.amountUSD||0),0);
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Trip Report - ${trip.name}</title>
  <style>body{font-family:Arial,sans-serif;max-width:800px;margin:0 auto;padding:40px;color:#111}
  h1{color:#22d3ee;border-bottom:2px solid #22d3ee;padding-bottom:12px}
  .meta{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8fafc;padding:16px;border-radius:8px}
  .meta div{font-size:13px}.meta strong{display:block;font-size:11px;color:#666;text-transform:uppercase;margin-bottom:4px}
  table{width:100%;border-collapse:collapse;margin-top:20px}
  th{background:#f1f5f9;padding:10px;text-align:left;font-size:12px;color:#666;text-transform:uppercase}
  td{padding:10px;border-bottom:1px solid #e2e8f0;font-size:13px}
  .total{text-align:right;padding:16px;font-size:16px;font-weight:700;color:#22d3ee}
  .status{display:inline-block;padding:3px 10px;border-radius:12px;font-size:11px;font-weight:600;background:#d1fae5;color:#065f46}
  .footer{margin-top:40px;font-size:11px;color:#94a3b8;text-align:center;border-top:1px solid #e2e8f0;padding-top:16px}
  </style></head><body>
  <h1>✈️ Trip Expense Report</h1>
  <div class="meta">
    <div><strong>Trip Name</strong>${trip.name}</div>
    <div><strong>Destination</strong>${trip.destination||"—"}</div>
    <div><strong>Employee</strong>${trip.employeeName}</div>
    <div><strong>Company</strong>${trip.company==="synopsis"?"Synopsis-Tech":"Incloudites"}</div>
    <div><strong>Dates</strong>${trip.startDate}${trip.endDate?" → "+trip.endDate:""}</div>
    <div><strong>Purpose</strong>${trip.purpose}</div>
    <div><strong>Status</strong><span class="status">${trip.status}</span></div>
    <div><strong>Budget</strong>${trip.budget>0?"$"+trip.budget.toFixed(2):"No limit"}</div>
    <div><strong>Approved by (Step 1)</strong>${a1}</div>
    <div><strong>Approved by (Step 2)</strong>${a2}</div>
  </div>
  <table>
    <thead><tr><th>#</th><th>Date</th><th>Category</th><th>Description</th><th>Vendor</th><th>Original</th><th>USD</th><th>Receipt</th></tr></thead>
    <tbody>
      ${tripExps.map((e,i)=>`<tr><td>${i+1}</td><td>${e.date}</td><td>${e.category}</td><td>${e.description}</td><td>${e.vendor||"—"}</td><td>${e.amount.toFixed(2)} ${e.currency}</td><td>$${(e.amountUSD||0).toFixed(2)}</td><td>${e.receiptUrl?"✓":"—"}</td></tr>`).join("")}
    </tbody>
  </table>
  <div class="total">Total: $${total.toFixed(2)} USD</div>
  <div class="footer">Generated by Expense.io · ${new Date().toLocaleDateString()} · Confidential</div>
  </body></html>`;
  const win = window.open("","_blank");
  win.document.write(html);
  win.document.close();
  win.print();
}


async function scanReceiptWithClaude(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(",")[1];
      const mediaType = file.type || "image/jpeg";
      try {
        const response = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: CLAUDE_MODEL,
            max_tokens: 1000,
            messages: [{
              role: "user",
              content: [
                { type: "image", source: { type: "base64", media_type: mediaType, data: base64 } },
                { type: "text", text: `Extract expense information from this receipt. Respond ONLY with a JSON object (no markdown, no backticks) with these fields:
{
  "amount": <number or null>,
  "currency": <3-letter currency code like USD EUR GBP MAD AED SAR or null>,
  "date": <YYYY-MM-DD format or null>,
  "vendor": <vendor/merchant name or null>,
  "description": <brief description of what was purchased or null>,
  "category": <one of: Food & Meals, Transport, Hotel, Flight, Subscription, Equipment, Training, Entertainment, Car Rental, Other>
}` }
              ]
            }]
          })
        });
        const data = await response.json();
        const text = data.content?.[0]?.text || "{}";
        const clean = text.replace(/```json|```/g, "").trim();
        resolve(JSON.parse(clean));
      } catch(e) {
        resolve(null);
      }
    };
    reader.readAsDataURL(file);
  });
}

// ─── R2 Upload ────────────────────────────────────────────────────────────────
async function uploadToR2(file, userId) {
  const ext = file.name.split(".").pop();
  const path = `${userId}/${Date.now()}.${ext}`;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("path", path);
  try {
    const res = await fetch(R2_WORKER, { method: "POST", body: formData });
    const data = await res.json();
    return data.url || null;
  } catch(e) {
    return null;
  }
}

// ─── Force Change Password ────────────────────────────────────────────────────
function ForceChangePwd({user,onSaved}) {
  const S=useS(); const t=S.t;
  const [p1,setP1]=useState(""); const [p2,setP2]=useState(""); const [err,setErr]=useState(""); const [ok,setOk]=useState(false); const [saving,setSaving]=useState(false);
  const save=async()=>{
    if(p1.length<8){setErr("Password must be at least 8 characters.");return;}
    if(p1!==p2){setErr("Passwords do not match.");return;}
    setSaving(true);
    // Update password in Supabase Auth
    const {error:authErr}=await supabase.auth.updateUser({password:p1});
    if(authErr){setErr("Error saving. Try again.");setSaving(false);return;}
    // Update must_change_pwd flag in users table
    await supabase.from("users").update({must_change_pwd:false}).eq("id",user.id);
    setSaving(false);
    setOk(true); setTimeout(()=>onSaved({...user,mustChangePwd:false}),1200);
  };
  return (
    <div style={{minHeight:"100vh",background:t.appBg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
      <div style={{width:420}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:34,fontWeight:800,color:t.text,letterSpacing:"-1.5px"}}>Expense<span style={{color:"#22d3ee"}}>.</span>io</div>
          <div style={{marginTop:10,padding:"10px 20px",background:"rgba(245,158,11,0.1)",borderRadius:10,border:"1px solid rgba(245,158,11,0.25)",fontSize:13,color:"#f59e0b"}}>🔐 Please set a new password before continuing</div>
        </div>
        <div style={{background:t.cardBg,border:`1px solid ${t.border}`,borderRadius:16,padding:32}}>
          <div style={{marginBottom:16,padding:"12px 16px",background:t.inputBg,borderRadius:10,fontSize:13,color:t.subText}}>Welcome, <strong style={{color:t.text}}>{user.name}</strong>! Choose a personal password to get started.</div>
          <div style={{marginBottom:16}}><label style={S.label}>New Password</label><input style={S.input} type="password" value={p1} onChange={e=>setP1(e.target.value)} placeholder="Min. 8 characters" /></div>
          <div style={{marginBottom:20}}><label style={S.label}>Confirm Password</label><input style={S.input} type="password" value={p2} onChange={e=>setP2(e.target.value)} placeholder="Repeat password" onKeyDown={e=>e.key==="Enter"&&save()} /></div>
          {err&&<div style={{color:"#ef4444",fontSize:13,marginBottom:14,background:"rgba(239,68,68,0.08)",padding:"10px 14px",borderRadius:8}}>{err}</div>}
          {ok&&<div style={{color:"#10b981",fontSize:13,marginBottom:14,background:"rgba(16,185,129,0.08)",padding:"10px 14px",borderRadius:8}}>✓ Password updated! Redirecting…</div>}
          <button style={{...S.btn,...S.btnPrimary,width:"100%",padding:12,fontSize:14,opacity:saving?0.7:1}} onClick={save}>{saving?"Saving…":"Set Password & Continue →"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Login ────────────────────────────────────────────────────────────────────
function LoginPage({onLogin}) {
  const S=useS(); const t=S.t;
  const [email,setEmail]=useState(""); const [pass,setPass]=useState(""); const [remember,setRemember]=useState(true); const [err,setErr]=useState(""); const [loading,setLoading]=useState(false);
  const submit=async()=>{
    setLoading(true); setErr("");
    // Step 1 — Sign in with Supabase Auth
    const {data:authData,error:authError}=await supabase.auth.signInWithPassword({
      email:email.trim().toLowerCase(), password:pass
    });
    if(authError||!authData.user){
      setLoading(false);
      setErr("Invalid email or password.");
      return;
    }
    // Step 2 — Fetch user profile from users table using auth_id
    const {data:profile,error:profileError}=await supabase.from("users").select("*").eq("auth_id",authData.user.id).single();
    setLoading(false);
    if(profileError||!profile){setErr("Account not found. Contact your admin.");return;}
    if(!profile.active){setErr("Your account is inactive. Contact your admin.");return;}
    const user=mapUser(profile);
    // Session is managed by Supabase Auth automatically
    // Store userId in localStorage only for remember-me UX
    if(remember){
      const session={userId:user.id,expiry:Date.now()+(7*24*60*60*1000)};
      localStorage.setItem("expenseio_session",JSON.stringify(session));
    }
    onLogin(user);
  };
  return (
    <div style={{minHeight:"100vh",background:t.appBg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
      <div style={{width:440}}>
        <div style={{textAlign:"center",marginBottom:36}}>
          <div style={{fontSize:34,fontWeight:800,color:t.text,letterSpacing:"-1.5px"}}>Expense<span style={{color:"#22d3ee"}}>.</span>io</div>
          <div style={{display:"flex",justifyContent:"center",gap:10,marginTop:12}}>{COMPANIES.map(c=><CompanyTag key={c.id} companyId={c.id} size="lg" />)}</div>
        </div>
        <div style={{background:t.cardBg,border:`1px solid ${t.border}`,borderRadius:16,padding:32}}>
          <div style={{marginBottom:18}}><label style={S.label}>Email</label><input style={S.input} type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@synopsis-tech.net" onKeyDown={e=>e.key==="Enter"&&submit()} /></div>
          <div style={{marginBottom:18}}><label style={S.label}>Password</label><input style={S.input} type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&submit()} /></div>
          <div style={{marginBottom:24,display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>setRemember(r=>!r)}>
            <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${remember?"#22d3ee":t.border}`,background:remember?"#22d3ee":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
              {remember&&<span style={{color:"#0b0e17",fontSize:12,fontWeight:900}}>✓</span>}
            </div>
            <span style={{fontSize:13,color:t.subText}}>Remember me for 7 days</span>
          </div>
          {err&&<div style={{color:"#ef4444",fontSize:13,marginBottom:16,background:"rgba(239,68,68,0.08)",padding:"10px 14px",borderRadius:8}}>{err}</div>}
          <button style={{...S.btn,...S.btnPrimary,width:"100%",padding:12,fontSize:14,opacity:loading?0.7:1}} onClick={submit}>{loading?"Signing in…":"Sign In →"}</button>
          <div style={{marginTop:16,padding:12,background:t.inputBg,borderRadius:8,fontSize:11.5,color:t.subText,textAlign:"center"}}>Contact your admin if you don't have login credentials yet.</div>
        </div>
      </div>
    </div>
  );
}

// ─── Profile ──────────────────────────────────────────────────────────────────
function ProfilePage({user,onUpdateUser}) {
  const S=useS(); const t=S.t;
  const [form,setForm]=useState({name:user.name,email:user.email,phone:user.phone||"",department:user.department||""});
  const [pwd,setPwd]=useState({old:"",p1:"",p2:""});
  const [saved,setSaved]=useState(false); const [pwdMsg,setPwdMsg]=useState(""); const [saving,setSaving]=useState(false);
  const saveProfile=async()=>{
    setSaving(true);
    const {error}=await supabase.from("users").update({name:form.name,email:form.email,phone:form.phone,department:form.department}).eq("id",user.id);
    setSaving(false);
    if(!error){onUpdateUser({...user,...form});setSaved(true);setTimeout(()=>setSaved(false),2500);}
  };
  const changePwd=async()=>{
    if(pwd.old!==user.password){setPwdMsg("Current password is incorrect.");return;}
    if(pwd.p1.length<8){setPwdMsg("New password must be at least 8 characters.");return;}
    if(pwd.p1!==pwd.p2){setPwdMsg("Passwords do not match.");return;}
    // Update in Supabase Auth
    const {error:authErr}=await supabase.auth.updateUser({password:pwd.p1});
    if(authErr){setPwdMsg("Error updating password. Try again.");return;}
    // Keep password field in users table in sync for admin reference
    await supabase.from("users").update({password:pwd.p1}).eq("id",user.id);
    onUpdateUser({...user,password:pwd.p1});
    setPwd({old:"",p1:"",p2:""});
    setPwdMsg("✓ Password changed!");
    setTimeout(()=>setPwdMsg(""),3000);
  };
  return (
    <div>
      <h2 style={S.h2}>My Profile</h2>
      <p style={S.sub}>Update your details or change your password anytime.</p>
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:24,padding:16,background:t.inputBg,borderRadius:12}}>
            <Avatar name={user.name} size={52} />
            <div><div style={{fontSize:16,fontWeight:700,color:t.text}}>{user.name}</div><div style={{display:"flex",gap:8,marginTop:6,flexWrap:"wrap"}}><CompanyTag companyId={user.company} /><RolePill role={user.role} /></div></div>
          </div>
          {[["Full Name","name","text","Jane Doe"],["Email","email","email","jane@company.com"],["Phone","phone","text","+212 600 000 000"]].map(([lbl,k,ty,ph])=>(
            <div key={k} style={{marginBottom:16}}><label style={S.label}>{lbl}</label><input style={S.input} type={ty} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={ph} /></div>
          ))}
          <div style={{marginBottom:20}}><label style={S.label}>Department</label><select style={S.input} value={form.department} onChange={e=>setForm(f=>({...f,department:e.target.value}))}>{DEPARTMENTS.map(d=><option key={d}>{d}</option>)}</select></div>
          {saved&&<div style={{color:"#10b981",fontSize:13,marginBottom:12,background:"rgba(16,185,129,0.08)",padding:"10px 14px",borderRadius:8}}>✓ Profile updated.</div>}
          <button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={saveProfile}>{saving?"Saving…":"Save Changes"}</button>
        </div>
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:20}}>🔐 Change Password</div>
          {[["Current Password","old"],["New Password","p1"],["Confirm New Password","p2"]].map(([lbl,k])=>(
            <div key={k} style={{marginBottom:16}}><label style={S.label}>{lbl}</label><input style={S.input} type="password" value={pwd[k]} onChange={e=>setPwd(p=>({...p,[k]:e.target.value}))} placeholder="••••••••" /></div>
          ))}
          {pwdMsg&&<div style={{fontSize:13,marginBottom:14,padding:"10px 14px",borderRadius:8,color:pwdMsg.startsWith("✓")?"#10b981":"#ef4444",background:pwdMsg.startsWith("✓")?"rgba(16,185,129,0.08)":"rgba(239,68,68,0.08)"}}>{pwdMsg}</div>}
          <button style={{...S.btn,...S.btnPrimary}} onClick={changePwd}>Update Password</button>
        </div>
      </div>
    </div>
  );
}

// ─── Analytics Dashboard ──────────────────────────────────────────────────────
function AnalyticsPage({user,expenses,trips,users}) {
  const S=useS(); const t=S.t;
  const [range,setRange]=useState("all"); const [company,setCompany]=useState("all");
  const now=new Date();
  const filterExp=expenses.filter(e=>{
    if(company!=="all"&&e.company!==company)return false;
    if(user.role==="employee"&&e.employeeId!==user.id)return false;
    if(range==="month"){const d=new Date(e.date);return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();}
    if(range==="quarter"){const d=new Date(e.date);const q=Math.floor(now.getMonth()/3);return Math.floor(d.getMonth()/3)===q&&d.getFullYear()===now.getFullYear();}
    if(range==="year"){return new Date(e.date).getFullYear()===now.getFullYear();}
    return true;
  });
  const totalUSD=filterExp.reduce((s,e)=>s+(e.amountUSD||0),0);
  const paid=filterExp.filter(e=>e.status==="paid").reduce((s,e)=>s+(e.amountUSD||0),0);
  const pending=filterExp.filter(e=>e.status==="pending"||e.status==="approved_by_m1").reduce((s,e)=>s+(e.amountUSD||0),0);
  // By category
  const cats=[...new Set(filterExp.map(e=>e.category))];
  const byCat=cats.map(c=>({name:c,usd:filterExp.filter(e=>e.category===c).reduce((s,e)=>s+(e.amountUSD||0),0)})).sort((a,b)=>b.usd-a.usd);
  const maxCat=Math.max(...byCat.map(c=>c.usd),1);
  // By employee (managers/admin only)
  const empIds=[...new Set(filterExp.map(e=>e.employeeId))];
  const byEmp=empIds.map(id=>({name:users.find(u=>u.id===id)?.name||id,usd:filterExp.filter(e=>e.employeeId===id).reduce((s,e)=>s+(e.amountUSD||0),0)})).sort((a,b)=>b.usd-a.usd).slice(0,8);
  const maxEmp=Math.max(...byEmp.map(e=>e.usd),1);
  // By month (last 6)
  const months=[];
  for(let i=5;i>=0;i--){const d=new Date(now.getFullYear(),now.getMonth()-i,1);months.push({key:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`,label:d.toLocaleString("default",{month:"short"})});}
  const byMonth=months.map(m=>({...m,usd:filterExp.filter(e=>e.date?.startsWith(m.key)).reduce((s,e)=>s+(e.amountUSD||0),0)}));
  const maxMonth=Math.max(...byMonth.map(m=>m.usd),1);

  const BAR_COLORS=["#22d3ee","#a78bfa","#10b981","#f59e0b","#f472b6","#6366f1","#34d399","#fb923c"];

  return (
    <div>
      <h2 style={S.h2}>📊 Analytics</h2>
      <p style={S.sub}>Spending insights across {user.role==="employee"?"your":"all"} expenses.</p>

      {/* Filters */}
      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
        {["all","month","quarter","year"].map(r=><button key={r} style={{...S.btn,...(range===r?S.btnPrimary:S.btnGhost),padding:"6px 14px",fontSize:12}} onClick={()=>setRange(r)}>{r==="all"?"All Time":r==="month"?"This Month":r==="quarter"?"This Quarter":"This Year"}</button>)}
        {user.role!=="employee"&&<>{["all","synopsis","incloudites"].map(c=><button key={c} style={{...S.btn,...(company===c?S.btnPrimary:S.btnGhost),padding:"6px 14px",fontSize:12}} onClick={()=>setCompany(c)}>{c==="all"?"All Companies":companyById(c).name}</button>)}</>}
      </div>

      {/* KPI Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:24}}>
        {[
          {l:"Total Spend",v:fmtUSD(totalUSD),c:"#22d3ee"},
          {l:"Paid",v:fmtUSD(paid),c:"#10b981"},
          {l:"Pending Payment",v:fmtUSD(pending),c:"#f59e0b"},
          {l:"Expenses",v:filterExp.length,c:"#a78bfa"},
        ].map(s=>(
          <div key={s.l} style={S.statCard}><div style={{fontSize:22,fontWeight:800,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:t.subText,marginTop:4}}>{s.l}</div></div>
        ))}
      </div>

      <div style={S.grid2}>
        {/* By Category */}
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:18}}>Spend by Category</div>
          {byCat.length===0?<div style={{color:t.subText,fontSize:13}}>No data.</div>:byCat.map((c,i)=>(
            <div key={c.name} style={{marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,marginBottom:5}}>
                <span style={{color:t.text,fontWeight:500}}>{c.name}</span>
                <span style={{color:BAR_COLORS[i%BAR_COLORS.length],fontWeight:700}}>{fmtUSD(c.usd)}</span>
              </div>
              <div style={{background:t.inputBg,borderRadius:6,height:8,overflow:"hidden"}}>
                <div style={{background:BAR_COLORS[i%BAR_COLORS.length],height:8,borderRadius:6,width:`${(c.usd/maxCat)*100}%`,transition:"width .5s"}} />
              </div>
            </div>
          ))}
        </div>

        {/* By Month */}
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:18}}>Monthly Spend (USD)</div>
          <div style={{display:"flex",alignItems:"flex-end",gap:8,height:160}}>
            {byMonth.map((m,i)=>(
              <div key={m.key} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{fontSize:10,color:"#22d3ee",fontWeight:700}}>{m.usd>0?`$${Math.round(m.usd)}`:""}</div>
                <div style={{width:"100%",background:"#22d3ee",borderRadius:"4px 4px 0 0",height:`${Math.max((m.usd/maxMonth)*120,2)}px`,opacity:0.8+i*0.03}} />
                <div style={{fontSize:10.5,color:t.subText,fontWeight:600}}>{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* By Employee (managers only) */}
      {user.role!=="employee"&&byEmp.length>0&&(
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:18}}>Spend by Employee</div>
          {byEmp.map((e,i)=>(
            <div key={e.name} style={{marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,marginBottom:5}}>
                <span style={{color:t.text,fontWeight:500}}>{e.name}</span>
                <span style={{color:BAR_COLORS[i%BAR_COLORS.length],fontWeight:700}}>{fmtUSD(e.usd)}</span>
              </div>
              <div style={{background:t.inputBg,borderRadius:6,height:8,overflow:"hidden"}}>
                <div style={{background:BAR_COLORS[i%BAR_COLORS.length],height:8,borderRadius:6,width:`${(e.usd/maxEmp)*100}%`,transition:"width .5s"}} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* By Company (admin only) */}
      {user.role==="admin"&&(
        <div style={S.grid2}>
          {COMPANIES.map(co=>{
            const ces=filterExp.filter(e=>e.company===co.id);
            const ctot=ces.reduce((s,e)=>s+(e.amountUSD||0),0);
            const cpd=ces.filter(e=>e.status==="paid").reduce((s,e)=>s+(e.amountUSD||0),0);
            return (
              <div key={co.id} style={{...S.card,border:`1px solid ${co.color}30`}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}><CompanyTag companyId={co.id} size="lg" /></div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                  {[{l:"Total",v:fmtUSD(ctot),c:co.color},{l:"Paid",v:fmtUSD(cpd),c:"#10b981"},{l:"Expenses",v:ces.length,c:"#a78bfa"}].map(s=>(
                    <div key={s.l} style={{background:t.inputBg,borderRadius:8,padding:12}}><div style={{fontSize:16,fontWeight:800,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:t.subText,marginTop:3}}>{s.l}</div></div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({user,expenses,trips,users}) {
  const S=useS(); const t=S.t;
  const myExp  = expenses.filter(e=>e.employeeId===user.id);
  const myTrips= trips.filter(tr=>tr.employeeId===user.id);
  const allExp  = user.role==="employee"?myExp:expenses;
  const allTrips= user.role==="employee"?myTrips:trips;
  const totalUSD=allExp.reduce((s,e)=>s+(e.amountUSD||0),0);
  const pendingExp=allExp.filter(e=>e.status==="pending"||e.status==="approved_by_m1").length;
  const pendingTrips=allTrips.filter(tr=>tr.status==="pending"||tr.status==="approved_by_m1").length;
  const flagged=allExp.filter(e=>e.flagged).length;
  const byCat=[...new Set(allExp.map(e=>e.category))].map(c=>({name:c,usd:allExp.filter(e=>e.category===c).reduce((s,e)=>s+(e.amountUSD||0),0)})).filter(c=>c.usd>0).sort((a,b)=>b.usd-a.usd);
  const recent=[...allExp].sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).slice(0,5);
  return (
    <div>
      <h2 style={S.h2}>Dashboard</h2>
      <p style={S.sub}>Welcome back, {user.name.split(" ")[0]} 👋{user.role==="employee"?" — showing your expenses only":""}</p>
      {user.role!=="employee"&&(
        <div style={{...S.grid2,marginBottom:20}}>
          {COMPANIES.map(co=>{
            const ces=allExp.filter(e=>e.company===co.id);
            const tot=ces.reduce((s,e)=>s+(e.amountUSD||0),0);
            return (
              <div key={co.id} style={{background:t.statBg,border:`1px solid ${co.color}25`,borderRadius:14,padding:22}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}><CompanyTag companyId={co.id} size="lg" /><span style={{fontSize:11,color:t.subText}}>{users.filter(u=>u.company===co.id&&u.active).length} active users</span></div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                  {[{l:"Total USD",v:fmtUSD(tot),c:co.color},{l:"Pending",v:ces.filter(e=>e.status==="pending"||e.status==="approved_by_m1").length,c:"#f59e0b"},{l:"Trips",v:allTrips.filter(tr=>tr.company===co.id).length,c:"#a78bfa"}].map(s=>(
                    <div key={s.l}><div style={{fontSize:20,fontWeight:800,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:t.subText,marginTop:3}}>{s.l}</div></div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:20}}>
        {[{l:"Total (USD)",v:fmtUSD(totalUSD),c:"#22d3ee"},{l:"Pending Expenses",v:pendingExp,c:"#f59e0b"},{l:"Pending Trips",v:pendingTrips,c:"#a78bfa"},{l:"Flagged Items",v:flagged,c:"#f97316"}].map(s=>(
          <div key={s.l} style={S.statCard}><div style={{fontSize:24,fontWeight:800,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:t.subText,marginTop:4}}>{s.l}</div></div>
        ))}
      </div>
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:16}}>By Category (USD)</div>
          {byCat.length===0?<div style={{color:t.subText,fontSize:13}}>No expenses yet.</div>:byCat.map(c=>(
            <div key={c.name} style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:5}}><span style={{color:t.subText}}>{c.name}</span><span style={{color:"#22d3ee",fontWeight:700}}>{fmtUSD(c.usd)}</span></div>
              <div style={{background:t.inputBg,borderRadius:4,height:5}}><div style={{background:"linear-gradient(90deg,#22d3ee,#a78bfa)",height:5,borderRadius:4,width:`${Math.min(100,(c.usd/(totalUSD||1))*100)}%`}} /></div>
            </div>
          ))}
        </div>
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:16}}>Recent Activity</div>
          {recent.length===0?<div style={{color:t.subText,fontSize:13}}>No expenses yet.</div>:recent.map(e=>{
            const flag=CURRENCIES.find(c=>c.code===e.currency)?.flag||"💵";
            return (
              <div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${t.border}`}}>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:18}}>{flag}</span>
                  <div>
                    <div style={{fontSize:13,color:t.text,fontWeight:500}}>{e.description}</div>
                    <div style={{display:"flex",gap:6,alignItems:"center",marginTop:3}}><CompanyTag companyId={e.company} /><span style={{fontSize:11,color:t.subText}}>{e.category}</span>{e.tripId&&<span style={{fontSize:10,color:"#a78bfa",background:"rgba(167,139,250,0.1)",padding:"1px 6px",borderRadius:8}}>✈ Trip</span>}</div>
                  </div>
                </div>
                <div style={{textAlign:"right"}}><div style={{fontSize:14,fontWeight:700,color:"#22d3ee"}}>{fmtUSD(e.amountUSD)}</div><Badge status={e.flagged?"flagged":e.status} /></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Submit Expense (with OCR + Policy) ───────────────────────────────────────
function SubmitExpense({user,toUSD,rates,categories,trips,policies,onSubmit,onGoToTrip}) {
  const S=useS(); const t=S.t;
  const myTrips=trips.filter(tr=>tr.employeeId===user.id&&tr.status==="draft");
  const [form,setForm]=useState({category:categories[0]||"Food & Meals",amount:"",currency:"EUR",date:new Date().toISOString().slice(0,10),description:"",vendor:"",tripId:""});
  const [ok,setOk]=useState(false); const [saving,setSaving]=useState(false);
  const [receipt,setReceipt]=useState(null); const [receiptPreview,setReceiptPreview]=useState(null);
  const [err,setErr]=useState(""); const [scanning,setScanning]=useState(false); const [scanned,setScanned]=useState(false);
  const set=k=>e=>setForm(f=>({...f,[k]:e.target.value}));
  const preview=form.amount?toUSD(parseFloat(form.amount)||0,form.currency):0;
  const rateDisp=form.currency!=="USD"&&rates[form.currency]?`1 ${form.currency} = ${fmtUSD(1/rates[form.currency])}`:null;
  const co=companyById(user.company);
  const selectedTrip=myTrips.find(tr=>tr.id===form.tripId);

  // Policy check
  const companyPolicies=policies.filter(p=>p.company===user.company);
  const catPolicy=companyPolicies.find(p=>p.category===form.category);
  const policyViolation=catPolicy&&preview>catPolicy.maxUsd?`⚠ Policy limit for ${form.category} is ${fmtUSD(catPolicy.maxUsd)}. Your expense is ${fmtUSD(preview)}.`:null;

  const handleReceipt=async e=>{
    const file=e.target.files[0]; if(!file)return;
    setReceipt(file); setScanned(false);
    if(file.type.startsWith("image/")){
      const r=new FileReader();
      r.onload=ev=>setReceiptPreview(ev.target.result);
      r.readAsDataURL(file);
    } else setReceiptPreview("pdf");
  };

  const scanReceipt=async()=>{
    if(!receipt||!receipt.type.startsWith("image/"))return;
    setScanning(true);
    const result=await scanReceiptWithClaude(receipt);
    setScanning(false);
    if(result){
      setForm(f=>({
        ...f,
        amount:result.amount?String(result.amount):f.amount,
        currency:result.currency&&CURRENCIES.find(c=>c.code===result.currency)?result.currency:f.currency,
        date:result.date||f.date,
        vendor:result.vendor||f.vendor,
        description:result.description||f.description,
        category:result.category&&categories.includes(result.category)?result.category:f.category,
      }));
      setScanned(true);
    }
  };

  const submit=async()=>{
    setErr("");
    if(!form.tripId){setErr("Please select a trip. If you don't have one yet, create a trip first.");return;}
    if(!form.amount||!form.description||!form.date){setErr("Please fill in amount, description and date.");return;}
    setSaving(true);
    const receiptUrl=receipt?await uploadToR2(receipt,user.id):null;
    const amt=parseFloat(form.amount);
    const exp={id:"e"+Date.now(),employeeId:user.id,employeeName:user.name,company:user.company,...form,amount:amt,amountUSD:toUSD(amt,form.currency),rateUsed:rates[form.currency]||1,status:"pending",m1ApprovedBy:null,m2ApprovedBy:null,m1Comment:"",m2Comment:"",pushedToZoho:false,paid:false,createdAt:new Date().toISOString().slice(0,10),receiptUrl,tripId:form.tripId,flagged:false,flagNote:""};
    const {error}=await supabase.from("expenses").insert(expToDb(exp));
    setSaving(false);
    if(!error){onSubmit(exp);setForm({category:categories[0]||"Food & Meals",amount:"",currency:"EUR",date:new Date().toISOString().slice(0,10),description:"",vendor:"",tripId:form.tripId});setReceipt(null);setReceiptPreview(null);setScanned(false);setOk(true);setTimeout(()=>setOk(false),3500);}
  };

  return (
    <div>
      <h2 style={S.h2}>New Expense</h2>
      <p style={S.sub}>Add an expense to a trip. No trip yet? <button style={{background:"none",border:"none",color:"#a78bfa",cursor:"pointer",fontWeight:700,fontSize:13,padding:0}} onClick={onGoToTrip}>✈ Create one first →</button></p>
      <div style={{...S.card,maxWidth:640}}>
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",background:co.bg,borderRadius:10,border:`1px solid ${co.color}30`,marginBottom:20}}>
          <div style={{fontSize:12,color:t.subText}}>Filed under</div><CompanyTag companyId={user.company} size="lg" />
          <div style={{fontSize:11,color:t.subText,marginLeft:"auto"}}>{co.domain}</div>
        </div>

        {/* Trip Selector */}
        <div style={{marginBottom:20}}>
          <label style={{...S.label,color:"#f97316"}}>✈ Trip * (required)</label>
          {myTrips.length===0?(
            <div style={{padding:"16px",background:"rgba(249,115,22,0.08)",borderRadius:10,border:"1px solid rgba(249,115,22,0.25)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div><div style={{fontSize:13,color:"#f97316",fontWeight:600}}>No trips found</div><div style={{fontSize:12,color:t.subText,marginTop:4}}>Create a trip before adding expenses.</div></div>
              <button style={{...S.btn,background:"#f97316",color:"#fff",flexShrink:0}} onClick={onGoToTrip}>✈ Create Trip →</button>
            </div>
          ):(
            <>
              <select style={{...S.input,borderColor:form.tripId?"#22d3ee":"#f97316"}} value={form.tripId} onChange={set("tripId")}>
                <option value="">— Select a trip —</option>
                {myTrips.map(tr=><option key={tr.id} value={tr.id}>{tr.name}{tr.destination?` · ${tr.destination}`:""} ({tr.startDate})</option>)}
              </select>
              {selectedTrip&&(
                <div style={{marginTop:8,padding:"10px 14px",background:"rgba(34,211,238,0.06)",borderRadius:8,border:"1px solid rgba(34,211,238,0.15)",display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:18}}>✈️</span>
                  <div>
                    <div style={{fontSize:13,color:"#22d3ee",fontWeight:600}}>{selectedTrip.name}</div>
                    <div style={{fontSize:11.5,color:t.subText}}>{selectedTrip.destination&&`📍 ${selectedTrip.destination} · `}{selectedTrip.startDate}{selectedTrip.endDate?` → ${selectedTrip.endDate}`:""} · {selectedTrip.purpose}</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Receipt Upload + OCR */}
        <div style={{marginBottom:20}}>
          <label style={S.label}>Receipt</label>
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            <label style={{...S.btn,...S.btnGhost,flex:1,textAlign:"center",cursor:"pointer",padding:"10px",borderRadius:8,border:`1px solid ${t.border}`}}>
              <input type="file" accept="image/*" onChange={handleReceipt} style={{display:"none"}} />
              📷 Camera
            </label>
            <label style={{...S.btn,...S.btnGhost,flex:1,textAlign:"center",cursor:"pointer",padding:"10px",borderRadius:8,border:`1px solid ${t.border}`}}>
              <input type="file" accept="image/*" onChange={handleReceipt} style={{display:"none"}} />
              🖼 Photos
            </label>
            <label style={{...S.btn,...S.btnGhost,flex:1,textAlign:"center",cursor:"pointer",padding:"10px",borderRadius:8,border:`1px solid ${t.border}`}}>
              <input type="file" accept=".pdf,application/pdf" onChange={handleReceipt} style={{display:"none"}} />
              📄 PDF
            </label>
          </div>
          {receiptPreview==="pdf"&&<div style={{padding:"10px 14px",background:t.inputBg,borderRadius:8,fontSize:13,color:"#22d3ee"}}>📄 {receipt.name}</div>}
          {receiptPreview&&receiptPreview!=="pdf"&&<div style={{textAlign:"center"}}><img src={receiptPreview} alt="receipt" style={{maxHeight:160,maxWidth:"100%",borderRadius:8,marginBottom:4}} /><div style={{fontSize:11.5,color:"#10b981"}}>✓ {receipt.name}</div></div>}
          <div style={{display:"flex",gap:8,marginTop:8}}>
            {receipt&&receipt.type.startsWith("image/")&&(
              <button style={{...S.btn,background:"linear-gradient(135deg,#22d3ee,#a78bfa)",color:"#0b0e17",fontWeight:700,opacity:scanning?0.7:1}} onClick={scanReceipt} disabled={scanning}>
                {scanning?"🔍 Scanning…":"🔍 Auto-scan Receipt (OCR)"}
              </button>
            )}
            {receipt&&<button style={{...S.btn,...S.btnGhost,fontSize:12,padding:"5px 12px"}} onClick={()=>{setReceipt(null);setReceiptPreview(null);setScanned(false);}}>✕ Remove</button>}
          </div>
          {scanned&&<div style={{marginTop:8,padding:"8px 14px",background:"rgba(16,185,129,0.08)",borderRadius:8,fontSize:12.5,color:"#10b981",border:"1px solid rgba(16,185,129,0.2)"}}>✓ Receipt scanned! Fields auto-filled — please review and adjust if needed.</div>}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18,marginBottom:18}}>
          <div><label style={S.label}>Category</label><select style={S.input} value={form.category} onChange={set("category")}>{categories.map(c=><option key={c}>{c}</option>)}</select></div>
          <div><label style={S.label}>Date</label><input style={S.input} type="date" value={form.date} onChange={set("date")} /></div>
        </div>
        <div style={{marginBottom:18}}>
          <label style={S.label}>Currency & Amount</label>
          <div style={{display:"flex",gap:10}}>
            <div style={{flex:"0 0 260px"}}><select style={S.input} value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))}>{CURRENCIES.map(c=><option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>)}</select></div>
            <input style={{...S.input,flex:1}} type="number" placeholder="0.00" value={form.amount} onChange={set("amount")} />
          </div>
          {form.amount&&parseFloat(form.amount)>0&&(
            <div style={{marginTop:10,padding:"14px 18px",background:"linear-gradient(135deg,rgba(34,211,238,0.07),rgba(167,139,250,0.07))",borderRadius:10,border:"1px solid rgba(34,211,238,0.15)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div><div style={{fontSize:11,color:t.subText,fontWeight:700,textTransform:"uppercase",letterSpacing:".5px",marginBottom:4}}>Converted to USD</div><div style={{fontSize:24,fontWeight:800,color:"#22d3ee"}}>{fmtUSD(preview)}</div></div>
              <div style={{textAlign:"right"}}>{rateDisp&&<div style={{fontSize:12,color:t.subText}}>{rateDisp}</div>}<div style={{fontSize:11,color:t.subText,opacity:.5,marginTop:4}}>Rate locked on submit</div></div>
            </div>
          )}
          {policyViolation&&(
            <div style={{marginTop:10,padding:"10px 14px",background:"rgba(239,68,68,0.08)",borderRadius:8,fontSize:12.5,color:"#ef4444",border:"1px solid rgba(239,68,68,0.25)"}}>
              {policyViolation} — this will be flagged for manager review.
            </div>
          )}
        </div>
        <div style={{marginBottom:18}}><label style={S.label}>Vendor (optional)</label><input style={S.input} placeholder="e.g. Air France, Marriott…" value={form.vendor} onChange={set("vendor")} /></div>
        <div style={{marginBottom:24}}><label style={S.label}>Description</label><textarea style={{...S.input,height:80,resize:"vertical"}} placeholder="What was this expense for?" value={form.description} onChange={set("description")} /></div>
        {err&&<div style={{color:"#f97316",fontSize:13,marginBottom:16,background:"rgba(249,115,22,0.08)",padding:"12px 16px",borderRadius:8,border:"1px solid rgba(249,115,22,0.25)"}}>⚠ {err}</div>}
        {ok&&<div style={{color:"#10b981",fontSize:13,marginBottom:16,background:"rgba(16,185,129,0.08)",padding:"12px 16px",borderRadius:8,border:"1px solid rgba(16,185,129,0.18)"}}>✓ Expense added to trip! Add another or go to All Trips to submit.</div>}
        <button style={{...S.btn,...S.btnPrimary,padding:"11px 24px",opacity:(saving||myTrips.length===0)?0.5:1}} onClick={submit}>{saving?"Saving…":"Add Expense to Trip →"}</button>
      </div>
    </div>
  );
}

// ─── Create Trip ──────────────────────────────────────────────────────────────
function TripCreate({user,onSave,editTrip}) {
  const S=useS(); const t=S.t;
  const isEdit=!!editTrip&&(editTrip.status==="flagged"||editTrip.status==="rejected");
  const [trip,setTrip]=useState(editTrip||{name:"",destination:"",purpose:"Client Visit",startDate:"",endDate:"",budget:""});
  const [saving,setSaving]=useState(false); const [ok,setOk]=useState(false); const [err,setErr]=useState("");
  const setT=k=>e=>setTrip(tr=>({...tr,[k]:e.target.value}));
  const co=companyById(user.company);

  const saveTrip=async()=>{
    if(!trip.name||!trip.startDate){setErr("Trip name and start date are required.");return;}
    setSaving(true); setErr("");
    if(isEdit){
      const updatedTrip={...editTrip,...trip,status:"draft",m1ApprovedBy:null,m2ApprovedBy:null,m1Comment:"",m2Comment:""};
      const {error}=await supabase.from("trips").update(tripToDb(updatedTrip)).eq("id",editTrip.id);
      setSaving(false);
      if(error){setErr("Error saving. Try again.");return;}
      setOk(true); setTimeout(()=>onSave(updatedTrip),1500);
    } else {
      const tripId="t"+Date.now();
      const tripObj={id:tripId,name:trip.name,destination:trip.destination,purpose:trip.purpose,startDate:trip.startDate,endDate:trip.endDate,budget:parseFloat(trip.budget)||0,employeeId:user.id,employeeName:user.name,company:user.company,status:"draft",m1ApprovedBy:null,m2ApprovedBy:null,m1Comment:"",m2Comment:"",createdAt:new Date().toISOString().slice(0,10)};
      const {error}=await supabase.from("trips").insert(tripToDb(tripObj));
      setSaving(false);
      if(error){setErr("Error saving. Try again.");return;}
      setOk(true); setTimeout(()=>onSave(tripObj),1500);
    }
  };

  return (
    <div>
      <h2 style={S.h2}>{isEdit?"✏️ Edit & Resubmit Trip":"✈️ New Trip"}</h2>
      <p style={S.sub}>{isEdit?"Update the trip details then go to All Trips to resubmit.":"Create the trip first. Then go to New Expense to add expenses."}</p>
      <div style={{...S.card,maxWidth:620}}>
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",background:co.bg,borderRadius:10,border:`1px solid ${co.color}30`,marginBottom:24}}>
          <div style={{fontSize:12,color:t.subText}}>Filed under</div>
          <CompanyTag companyId={user.company} size="lg" />
          <div style={{fontSize:11,color:t.subText,marginLeft:"auto"}}>{co.domain}</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
          <div style={{gridColumn:"1/-1"}}><label style={S.label}>Trip Name *</label><input style={S.input} placeholder="e.g. Dubai Sales Trip Q2 2026" value={trip.name} onChange={setT("name")} /></div>
          <div style={{gridColumn:"1/-1"}}><label style={S.label}>Destination</label><input style={S.input} placeholder="e.g. Dubai, UAE" value={trip.destination} onChange={setT("destination")} /></div>
          <div><label style={S.label}>Start Date *</label><input style={S.input} type="date" value={trip.startDate} onChange={setT("startDate")} /></div>
          <div><label style={S.label}>End Date</label><input style={S.input} type="date" value={trip.endDate} onChange={setT("endDate")} /></div>
          <div><label style={S.label}>Purpose</label><select style={S.input} value={trip.purpose} onChange={setT("purpose")}>{PURPOSES.map(p=><option key={p}>{p}</option>)}</select></div>
          <div><label style={S.label}>Budget (USD, optional)</label><input style={S.input} type="number" placeholder="0.00" value={trip.budget} onChange={setT("budget")} /></div>
        </div>
        {err&&<div style={{color:"#ef4444",fontSize:13,marginBottom:14,background:"rgba(239,68,68,0.08)",padding:"10px 14px",borderRadius:8}}>{err}</div>}
        {ok&&<div style={{color:"#10b981",fontSize:13,marginBottom:14,background:"rgba(16,185,129,0.08)",padding:"12px 16px",borderRadius:8,border:"1px solid rgba(16,185,129,0.2)"}}>✓ Trip {isEdit?"updated":"created"}! Redirecting…<br/><span style={{fontSize:12,color:t.subText}}>Now go to New Expense to add expenses to this trip.</span></div>}
        <button style={{...S.btn,...S.btnPrimary,padding:"11px 28px",opacity:saving?0.7:1}} onClick={saveTrip}>{saving?"Saving…":"Save Trip →"}</button>
      </div>
      <div style={{...S.card,maxWidth:620,background:t.statBg}}>
        <div style={{fontSize:13,fontWeight:700,color:t.subText,marginBottom:14}}>HOW IT WORKS</div>
        {[
          {n:"1",icon:"✈️",title:"Create Trip",desc:"Fill in trip details and save."},
          {n:"2",icon:"＋",title:"Add Expenses",desc:"Go to New Expense, select this trip, add each expense with receipt."},
          {n:"3",icon:"🗂",title:"Submit Trip",desc:"Go to All Trips, open the trip, click Submit for Approval."},
          {n:"4",icon:"✅",title:"Approval & Payment",desc:"Managers review and approve. Finance pays in USD."},
        ].map(s=>(
          <div key={s.n} style={{display:"flex",gap:14,marginBottom:14,alignItems:"flex-start"}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:"rgba(34,211,238,0.1)",border:"1px solid rgba(34,211,238,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{s.icon}</div>
            <div><div style={{fontSize:13,fontWeight:700,color:t.text}}>{s.title}</div><div style={{fontSize:12,color:t.subText,marginTop:3}}>{s.desc}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Trip List ────────────────────────────────────────────────────────────────
function TripList({user,trips,expenses,onSelect,onEdit,onSubmitTrip,onDeleteTrip,onDeleteExp}) {
  const S=useS(); const t=S.t;
  const [filter,setFilter]=useState("all");
  const [expanded,setExpanded]=useState(null);
  const mine=user.role==="employee"?trips.filter(tr=>tr.employeeId===user.id):trips;
  const visible=filter==="all"?mine:mine.filter(tr=>tr.status===filter);

  return (
    <div>
      <h2 style={S.h2}>🗂 Trips</h2>
      <p style={S.sub}>Review your trip expenses and submit for approval when ready.</p>
      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
        {["all","draft","pending","approved_by_m1","approved","flagged","rejected","paid"].map(f=>(
          <button key={f} style={{...S.btn,...(filter===f?S.btnPrimary:S.btnGhost),padding:"6px 14px",fontSize:12}} onClick={()=>setFilter(f)}>
            {f==="all"?"All":f==="draft"?"Draft":STATUS_META[f]?.label||f}
          </button>
        ))}
      </div>
      {visible.length===0&&<div style={{...S.card,textAlign:"center",color:t.subText,padding:40}}>No trips found.</div>}
      {visible.map(tr=>{
        const tripExps=expenses.filter(e=>e.tripId===tr.id);
        const totalUSD=tripExps.reduce((s,e)=>s+(e.amountUSD||0),0);
        const flaggedCount=tripExps.filter(e=>e.flagged).length;
        const isExpanded=expanded===tr.id;
        const isDraft=tr.status==="draft";
        const canSubmit=isDraft&&tripExps.length>0&&tr.employeeId===user.id;
        const co=companyById(tr.company);
        return (
          <div key={tr.id} style={{...S.card,border:`1px solid ${isExpanded?co.color+"40":t.border}`,marginBottom:12,padding:0,overflow:"hidden"}}>
            <div style={{padding:"18px 20px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}} onClick={()=>setExpanded(isExpanded?null:tr.id)}>
              <div style={{display:"flex",gap:14,alignItems:"center"}}>
                <div style={{fontSize:24}}>✈️</div>
                <div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <span style={{fontSize:15,fontWeight:700,color:t.text}}>{tr.name}</span>
                    <CompanyTag companyId={tr.company} />
                    <Badge status={tr.status} />
                    {isDraft&&<span style={{fontSize:11,color:"#f59e0b",background:"rgba(245,158,11,0.1)",padding:"2px 8px",borderRadius:8,fontWeight:700}}>DRAFT</span>}
                  </div>
                  <div style={{fontSize:12,color:t.subText,marginTop:4,display:"flex",gap:12,flexWrap:"wrap"}}>
                    {tr.destination&&<span>📍 {tr.destination}</span>}
                    <span>📅 {tr.startDate}{tr.endDate?` → ${tr.endDate}`:""}</span>
                    <span>🎯 {tr.purpose}</span>
                    <span>{tripExps.length} expense{tripExps.length!==1?"s":""}</span>
                    {flaggedCount>0&&<span style={{color:"#f97316"}}>⚠ {flaggedCount} flagged</span>}
                  </div>
                </div>
              </div>
              <div style={{display:"flex",gap:12,alignItems:"center",flexShrink:0}}>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:18,fontWeight:800,color:"#22d3ee"}}>{fmtUSD(totalUSD)}</div>
                  {tr.budget>0&&<div style={{fontSize:11,color:totalUSD>tr.budget?"#ef4444":"#10b981"}}>Budget: {fmtUSD(tr.budget)}</div>}
                </div>
                <span style={{color:t.subText,fontSize:20,display:"inline-block",transition:"transform .2s",transform:isExpanded?"rotate(180deg)":"rotate(0deg)"}}>⌄</span>
              </div>
            </div>
            {isExpanded&&(
              <div style={{borderTop:`1px solid ${t.border}`,padding:"0 20px 20px"}}>
                {tr.destination&&isDraft&&(
                  <div style={{padding:"12px 0",borderBottom:`1px solid ${t.border}`,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                    <span style={{fontSize:11,color:t.subText,fontWeight:700,textTransform:"uppercase",letterSpacing:".5px"}}>Book:</span>
                    {[
                      {label:"✈️ Flights",url:`https://www.google.com/travel/flights#flt=/${encodeURIComponent(tr.destination)}/${(tr.startDate||"").replace(/-/g,"")}/${(tr.endDate||tr.startDate||"").replace(/-/g,"")}`,color:"#22d3ee"},
                      {label:"🏨 Hotels",url:`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(tr.destination)}&checkin=${tr.startDate||""}&checkout=${tr.endDate||""}`,color:"#a78bfa"},
                      {label:"🚗 Car",url:`https://www.google.com/travel/search?q=car+rental+${encodeURIComponent(tr.destination)}`,color:"#10b981"},
                    ].map(l=>(
                      <a key={l.label} href={l.url} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{padding:"4px 10px",borderRadius:6,background:`${l.color}15`,border:`1px solid ${l.color}30`,color:l.color,fontSize:11.5,fontWeight:600,textDecoration:"none"}}>{l.label}</a>
                    ))}
                  </div>
                )}
                {tripExps.length===0?(
                  <div style={{padding:"20px 0",color:t.subText,fontSize:13,textAlign:"center"}}>
                    No expenses yet. Go to <strong style={{color:"#22d3ee"}}>New Expense</strong> and select this trip.
                  </div>
                ):(
                  <>
                    {tripExps.map(e=>{
                      const flag=CURRENCIES.find(c=>c.code===e.currency)?.flag||"";
                      return (
                        <div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:`1px solid ${t.border}`,opacity:e.flagged?0.7:1}}>
                          <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                            <span style={{fontSize:18,marginTop:2}}>{flag}</span>
                            <div>
                              <div style={{fontSize:13,color:t.text,fontWeight:500}}>{e.description}</div>
                              <div style={{fontSize:11.5,color:t.subText,marginTop:2}}>{e.category} · {e.date} · {fmt(e.amount)} {e.currency}</div>
                              {e.flagNote&&<div style={{fontSize:11.5,color:"#f97316",marginTop:3}}>⚠ {e.flagNote}</div>}
                              {e.receiptUrl&&<div style={{marginTop:4}}>
                                {e.receiptUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                                  ?<img src={e.receiptUrl} alt="receipt" style={{maxHeight:60,borderRadius:4}} />
                                  :<a href={e.receiptUrl} target="_blank" rel="noreferrer" style={{fontSize:11.5,color:"#22d3ee"}}>📄 Receipt</a>}
                              </div>}
                            </div>
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <div style={{fontSize:14,fontWeight:700,color:"#22d3ee"}}>{fmtUSD(e.amountUSD)}</div>
                            <Badge status={e.flagged?"flagged":e.status} />
                            {isDraft&&tr.employeeId===user.id&&(
                              <button style={{...S.btn,...S.btnDanger,padding:"3px 8px",fontSize:11,marginTop:4,display:"block"}} onClick={async ev=>{ev.stopPropagation();await supabase.from("expenses").delete().eq("id",e.id);onDeleteExp(e.id);}}>✕ Remove</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:14,flexWrap:"wrap",gap:8}}>
                      <div style={{fontSize:13,color:t.subText}}>{tripExps.length} expenses · <span style={{color:"#22d3ee",fontWeight:700}}>{fmtUSD(totalUSD)}</span></div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {(tr.status==="flagged"||tr.status==="rejected")&&tr.employeeId===user.id&&(
                          <button style={{...S.btn,...S.btnWarn,padding:"6px 14px",fontSize:12}} onClick={e=>{e.stopPropagation();onEdit(tr);}}>✏️ Fix & Resubmit</button>
                        )}
                        {(user.role==="manager1"||user.role==="manager2"||user.role==="admin")&&(tr.status==="pending"||tr.status==="approved_by_m1"||tr.status==="approved")&&(
                          <button style={{...S.btn,...S.btnZoho,padding:"6px 14px",fontSize:12}} onClick={()=>onSelect(tr)}>Review Trip →</button>
                        )}
                        {canSubmit&&(
                          <button style={{...S.btn,...S.btnPrimary,padding:"6px 16px",fontSize:12}} onClick={()=>onSubmitTrip(tr)}>Submit for Approval →</button>
                        )}
                        {isDraft&&tripExps.length===0&&tr.employeeId===user.id&&(
                          <span style={{fontSize:12,color:t.subText,padding:"6px 0"}}>Add expenses first</span>
                        )}
                        {isDraft&&tr.employeeId===user.id&&(
                          <button style={{...S.btn,...S.btnDanger,padding:"6px 14px",fontSize:12}} onClick={async e=>{e.stopPropagation();if(window.confirm("Move this trip to trash? You can recover it within 30 days.")){const deletedAt=new Date().toISOString();await supabase.from("trips").update({deleted_at:deletedAt}).eq("id",tr.id);await supabase.from("expenses").update({deleted_at:deletedAt}).eq("trip_id",tr.id);onDeleteTrip(tr.id);}}}>🗑 Delete</button>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {/* Admin actions — always visible regardless of expense count */}
                {user.role==="admin"&&(
                  <div style={{display:"flex",gap:8,paddingTop:14,borderTop:`1px solid ${t.border}`,marginTop:4,justifyContent:"flex-end"}}>
                    <div style={{fontSize:12,color:t.subText,alignSelf:"center",marginRight:"auto"}}>Admin:</div>
                    <button style={{...S.btn,...S.btnWarn,padding:"6px 14px",fontSize:12}} onClick={async e=>{
                      e.stopPropagation();
                      if(window.confirm(`Move "${tr.name}" to trash? Recoverable within 30 days.`)){
                        const deletedAt=new Date().toISOString();
                        await supabase.from("trips").update({deleted_at:deletedAt}).eq("id",tr.id);
                        await supabase.from("expenses").update({deleted_at:deletedAt}).eq("trip_id",tr.id);
                        onDeleteTrip(tr.id);
                      }
                    }}>🗃 Move to Trash</button>
                    <button style={{...S.btn,...S.btnDanger,padding:"6px 14px",fontSize:12}} onClick={async e=>{
                      e.stopPropagation();
                      if(window.confirm(`⚠️ PERMANENT DELETE\n\nThis will permanently delete "${tr.name}" and ALL its expenses.\n\nThis CANNOT be undone. Are you sure?`)){
                        await supabase.from("expenses").delete().eq("trip_id",tr.id);
                        await supabase.from("trips").delete().eq("id",tr.id);
                        onDeleteTrip(tr.id);
                      }
                    }}>🗑 Delete Forever</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Trip Detail Modal ────────────────────────────────────────────────────────
function TripDetail({trip,expenses,users,user,settings,appConfig,onClose,onUpdate,onUpdateExp}) {
  const S=useS(); const t=S.t;
  const [comment,setComment]=useState(""); const [saving,setSaving]=useState(false);
  const [flagModal,setFlagModal]=useState(null); const [flagNote,setFlagNote]=useState("");
  const tripExps=expenses.filter(e=>e.tripId===trip.id);
  const totalUSD=tripExps.reduce((s,e)=>s+(e.amountUSD||0),0);
  const flaggedCount=tripExps.filter(e=>e.flagged).length;
  const companySetting=settings.find(s=>s.company===trip.company)||{};
  const settingsConfigured=!!(companySetting.approver1Id);
  const isApprover1=settingsConfigured?user.id===companySetting.approver1Id:user.role==="manager1";
  const isApprover2=settingsConfigured&&companySetting.approver2Id?user.id===companySetting.approver2Id:user.role==="manager2";
  const canM1=isApprover1&&trip.status==="pending";
  const canM2=companySetting.approver2Id?(isApprover2&&trip.status==="approved_by_m1"):(user.role==="admin"&&trip.status==="approved_by_m1");
  const canRej=(isApprover1&&trip.status==="pending")||(isApprover2&&trip.status==="approved_by_m1");
  const canPay=user.role==="admin"&&trip.status==="approved";
  const a1Name=users.find(u=>u.id===companySetting.approver1Id)?.name||"Approver 1";
  const a2Name=users.find(u=>u.id===companySetting.approver2Id)?.name||"Approver 2";
  const m1n=users.find(u=>u.id===trip.m1ApprovedBy)?.name;
  const m2n=users.find(u=>u.id===trip.m2ApprovedBy)?.name;

  const sendNotif=async(type,to,data)=>{
    console.log("TripDetail sendNotif:", type, to);
    try{await supabase.functions.invoke("send-notification",{body:{type,to,data}});}
    catch(e){console.log("Notif error:",e);}
  };

  const actTrip=async type=>{
    setSaving(true);
    let updated={...trip};
    const singleApprover=!companySetting.approver2Id;
    if(type==="m1"){
      if(singleApprover){
        updated.status="approved";updated.m1ApprovedBy=user.id;updated.m1Comment=comment;
        updated.m2ApprovedBy=user.id;updated.m2Comment="Single approver flow";
      } else {
        updated.status="approved_by_m1";updated.m1ApprovedBy=user.id;updated.m1Comment=comment;
      }
    }
    if(type==="m2"){updated.status="approved";updated.m2ApprovedBy=user.id;updated.m2Comment=comment;}
    if(type==="rej"){updated.status="rejected";if(isApprover1)updated.m1Comment=comment;else updated.m2Comment=comment;}
    if(type==="pay"){updated.status="paid";}
    await supabase.from("trips").update(tripToDb(updated)).eq("id",trip.id);
    if(type==="pay"){await supabase.from("expenses").update({status:"paid"}).eq("trip_id",trip.id);}
    setSaving(false); onUpdate(updated); setComment("");
    const baseData={tripName:trip.name,destination:trip.destination,employeeName:trip.employeeName,expenseCount:tripExps.length,totalUSD:totalUSD.toFixed(2),approverName:user.name,comment};
    const approver2=users.find(u=>u.id===companySetting.approver2Id);
    const employee=users.find(u=>u.id===trip.employeeId);
    const adminUser=users.find(u=>u.role==="admin");
    if(type==="m1"){
      if(singleApprover){
        if(adminUser?.email&&appConfig?.notifyOnM2Approve) sendNotif("trip_approved_m2",adminUser.email,baseData);
        if(employee?.email&&appConfig?.notifyOnM2Approve) sendNotif("trip_approved_m1",employee.email,{...baseData,message:"Your trip has been approved and is awaiting payment."});
      } else {
        if(approver2?.email&&appConfig?.notifyOnM1Approve) sendNotif("trip_approved_m1",approver2.email,baseData);
      }
    }
    if(type==="m2"){
      if(adminUser?.email&&appConfig?.notifyOnM2Approve) sendNotif("trip_approved_m2",adminUser.email,baseData);
      if(employee?.email&&appConfig?.notifyOnM2Approve) sendNotif("trip_approved_m1",employee.email,{...baseData,message:"Your trip has been fully approved and is awaiting payment."});
      if(appConfig?.copyApproverOnFinalApproval){const a1=users.find(u=>u.id===companySetting.approver1Id);if(a1?.email)sendNotif("trip_approved_m1",a1.email,{...baseData,message:"FYI: Trip you approved has received final approval."});}
    }
    if(type==="rej"&&employee?.email&&appConfig?.notifyOnReject) sendNotif("trip_rejected",employee.email,baseData);
    if(type==="pay"){
      if(employee?.email&&appConfig?.notifyOnPaid) sendNotif("trip_paid",employee.email,baseData);
      if(approver2?.email&&appConfig?.notifyOnPaid) sendNotif("trip_paid",approver2.email,baseData);
      onClose();
    }
  };

  const flagExpense=async()=>{
    if(!flagNote.trim())return;
    const exp=flagModal;
    const updated={...exp,flagged:true,flagNote};
    await supabase.from("expenses").update({flagged:true,flag_note:flagNote}).eq("id",exp.id);
    const updatedTrip={...trip,status:"flagged"};
    await supabase.from("trips").update({status:"flagged"}).eq("id",trip.id);
    onUpdateExp(updated); onUpdate(updatedTrip);
    const employee=users.find(u=>u.id===trip.employeeId);
    if(employee?.email) sendNotif("trip_rejected",employee.email,{tripName:trip.name,destination:trip.destination,employeeName:trip.employeeName,expenseCount:tripExps.length,totalUSD:totalUSD.toFixed(2),approverName:user.name,comment:`Expense flagged: ${flagNote}`});
    setFlagModal(null); setFlagNote("");
  };

  const Step=({num,label,done,active,by,comment:c})=>(
    <div style={{display:"flex",gap:14,marginBottom:14,alignItems:"flex-start"}}>
      <div style={{width:28,height:28,borderRadius:"50%",background:done?"#10b981":active?"#22d3ee":t.inputBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:done||active?"#fff":t.subText,flexShrink:0,marginTop:2}}>{done?"✓":num}</div>
      <div><div style={{fontSize:13,fontWeight:600,color:t.text}}>{label}</div>
        {by&&<div style={{fontSize:12,color:"#10b981",marginTop:2}}>✓ {by}{c?` — "${c}"`:""}</div>}
        {!by&&<div style={{fontSize:12,color:active?"#22d3ee":t.subText,marginTop:2}}>{active?"Awaiting approval…":"Not yet reached"}</div>}
      </div>
    </div>
  );

  return (
    <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.modalBox}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:t.text}}>✈️ {trip.name}</div>
            <div style={{display:"flex",gap:8,alignItems:"center",marginTop:6,flexWrap:"wrap"}}>
              <CompanyTag companyId={trip.company} size="lg" />
              {trip.destination&&<span style={{fontSize:12,color:t.subText}}>📍 {trip.destination}</span>}
              <span style={{fontSize:12,color:t.subText}}>{trip.startDate}{trip.endDate?` → ${trip.endDate}`:""}</span>
              <Badge status={trip.status} />
            </div>
          </div>
          <button style={{background:"none",border:"none",color:t.subText,cursor:"pointer",fontSize:20}} onClick={onClose}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:20}}>
          {[{l:"Total USD",v:fmtUSD(totalUSD),c:"#22d3ee"},{l:"Expenses",v:tripExps.length,c:t.text},{l:"Flagged",v:flaggedCount,c:"#f97316"},{l:"Purpose",v:trip.purpose,c:"#a78bfa"}].map(s=>(
            <div key={s.l} style={{background:t.inputBg,borderRadius:8,padding:12}}><div style={{fontSize:16,fontWeight:800,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:t.subText,marginTop:3}}>{s.l}</div></div>
          ))}
        </div>
        {trip.budget>0&&<div style={{marginBottom:16,padding:"8px 14px",background:totalUSD>trip.budget?"rgba(239,68,68,0.08)":"rgba(16,185,129,0.08)",borderRadius:8,fontSize:12,color:totalUSD>trip.budget?"#ef4444":"#10b981"}}>Budget: {fmtUSD(trip.budget)} · Spent: {fmtUSD(totalUSD)} · {totalUSD>trip.budget?"⚠ Over budget":"✓ Within budget"}</div>}

        {/* Travel Booking Links */}
        {trip.destination&&(()=>{
          const dest=encodeURIComponent(trip.destination);
          const start=trip.startDate||"";
          const end=trip.endDate||trip.startDate||"";
          const startFmt=start.replace(/-/g,"");
          const endFmt=end.replace(/-/g,"");
          const googleFlights=`https://www.google.com/travel/flights?q=flights+to+${dest}&tfs=CBwQAhoeEgoyMDI1LTAxLTAxKgIIAUICCAFqDQgCEgkvbS8wOWN0XzIaHhIKMjAyNS0wMS0wOCoCCAFCAgfABGoNCgISCS9tLzA5Y3RfMg==`;
          const flightUrl=`https://www.google.com/travel/flights#flt=/${dest}/${startFmt}/${endFmt}`;
          const bookingUrl=`https://www.booking.com/searchresults.html?ss=${dest}&checkin=${start}&checkout=${end}&group_adults=1`;
          const mapsUrl=`https://www.google.com/maps/search/${dest}`;
          const viatorUrl=`https://www.viator.com/search/${dest}`;
          return (
            <div style={{marginBottom:20,padding:16,background:`linear-gradient(135deg,rgba(34,211,238,0.06),rgba(167,139,250,0.06))`,borderRadius:12,border:`1px solid rgba(34,211,238,0.15)`}}>
              <div style={{fontSize:12,fontWeight:700,color:t.subText,marginBottom:12,textTransform:"uppercase",letterSpacing:".5px"}}>✈ Book Travel for {trip.destination}</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <a href={flightUrl} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:8,background:"rgba(34,211,238,0.1)",border:"1px solid rgba(34,211,238,0.25)",color:"#22d3ee",fontSize:12,fontWeight:600,textDecoration:"none"}}>
                  ✈️ Search Flights
                </a>
                <a href={bookingUrl} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:8,background:"rgba(167,139,250,0.1)",border:"1px solid rgba(167,139,250,0.25)",color:"#a78bfa",fontSize:12,fontWeight:600,textDecoration:"none"}}>
                  🏨 Find Hotels
                </a>
                <a href={`https://www.google.com/travel/search?q=car+rental+${dest}`} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:8,background:"rgba(16,185,129,0.1)",border:"1px solid rgba(16,185,129,0.25)",color:"#10b981",fontSize:12,fontWeight:600,textDecoration:"none"}}>
                  🚗 Car Rental
                </a>
                <a href={mapsUrl} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:8,background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.25)",color:"#f59e0b",fontSize:12,fontWeight:600,textDecoration:"none"}}>
                  🗺 Map
                </a>
              </div>
              <div style={{fontSize:11,color:t.subText,marginTop:8}}>Opens in a new tab · Dates: {start}{end&&end!==start?` → ${end}`:""}</div>
            </div>
          );
        })()}


        <div style={{background:t.inputBg,borderRadius:10,padding:16,marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:t.subText,marginBottom:12,textTransform:"uppercase",letterSpacing:".5px"}}>Trip Expenses</div>
          {tripExps.map(e=>{
            const flag=CURRENCIES.find(c=>c.code===e.currency)?.flag||"";
            return (
              <div key={e.id} style={{padding:"11px 0",borderBottom:`1px solid ${t.border}`,opacity:e.flagged?0.7:1}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <span style={{fontSize:13,color:t.text,fontWeight:500}}>{e.description}</span>
                      {e.flagged&&<span style={{fontSize:11,color:"#f97316",background:"rgba(249,115,22,0.1)",padding:"2px 8px",borderRadius:8,fontWeight:700}}>⚠ Flagged</span>}
                    </div>
                    <div style={{fontSize:11.5,color:t.subText,marginTop:2}}>{e.category} · {e.date} · {flag} {fmt(e.amount)} {e.currency} → <span style={{color:"#22d3ee",fontWeight:700}}>{fmtUSD(e.amountUSD)}</span></div>
                    {e.flagNote&&<div style={{fontSize:12,color:"#f97316",marginTop:4,padding:"4px 10px",background:"rgba(249,115,22,0.08)",borderRadius:6}}>Note: {e.flagNote}</div>}
                    {e.receiptUrl&&(
                      <div style={{marginTop:6}}>
                        {e.receiptUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                          ?<img src={e.receiptUrl} alt="receipt" style={{maxHeight:80,borderRadius:6}} />
                          :<a href={e.receiptUrl} target="_blank" rel="noreferrer" style={{fontSize:12,color:"#22d3ee"}}>📄 View Receipt</a>}
                      </div>
                    )}
                  </div>
                  {(canM1||canM2)&&!e.flagged&&(
                    <button style={{...S.btn,...S.btnWarn,padding:"4px 10px",fontSize:11.5,marginLeft:10,flexShrink:0}} onClick={()=>setFlagModal(e)}>⚠ Flag</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {trip.status==="pending"&&!trip.m1ApprovedBy&&(
          <div style={{marginBottom:16,padding:"10px 16px",background:"rgba(99,102,241,0.08)",borderRadius:8,fontSize:13,color:"#818cf8",border:"1px solid rgba(99,102,241,0.2)",display:"flex",alignItems:"center",gap:8}}>
            🔄 <strong>Resubmitted for review</strong> — the employee has updated and resubmitted this trip.
          </div>
        )}
        <div style={{background:t.inputBg,borderRadius:10,padding:18,marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:700,color:t.subText,marginBottom:14,textTransform:"uppercase",letterSpacing:".5px"}}>Approval Flow</div>
          <Step num={1} label={a1Name} done={!!trip.m1ApprovedBy&&trip.status!=="rejected"&&trip.status!=="flagged"} active={trip.status==="pending"} by={m1n} comment={trip.m1Comment} />
          <Step num={2} label={a2Name} done={!!trip.m2ApprovedBy} active={trip.status==="approved_by_m1"} by={m2n} comment={trip.m2Comment} />
          <Step num={3} label="Finance — Pay in USD" done={trip.status==="paid"} active={trip.status==="approved"} by={trip.status==="paid"?"Admin Finance":null} />
        </div>
        {(canM1||canM2||canRej)&&<div style={{marginBottom:18}}><label style={S.label}>Comment (optional)</label><textarea style={{...S.input,height:56,resize:"none"}} placeholder="Add a note…" value={comment} onChange={e=>setComment(e.target.value)} /></div>}
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {canM1&&<button style={{...S.btn,...S.btnSuccess,opacity:saving?0.7:1}} onClick={()=>actTrip("m1")}>✓ Approve Trip</button>}
          {canM2&&<button style={{...S.btn,...S.btnSuccess,opacity:saving?0.7:1}} onClick={()=>actTrip("m2")}>✓ Final Approve</button>}
          {canRej&&<button style={{...S.btn,...S.btnDanger,opacity:saving?0.7:1}} onClick={()=>actTrip("rej")}>✕ Reject Trip</button>}
          {canPay&&<button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={()=>actTrip("pay")}>💳 Pay {fmtUSD(totalUSD)}</button>}
          <button style={{...S.btn,...S.btnGhost}} onClick={()=>exportTripPDF(trip,tripExps,users)}>📄 Export PDF</button>
        </div>
      </div>
      {flagModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}} onClick={e=>e.target===e.currentTarget&&setFlagModal(null)}>
          <div style={S.modalSm}>
            <div style={{fontSize:16,fontWeight:700,color:t.text,marginBottom:6}}>⚠ Flag Expense</div>
            <div style={{fontSize:13,color:t.subText,marginBottom:16}}>"{flagModal.description}" · {fmtUSD(flagModal.amountUSD)}</div>
            <div style={{marginBottom:16}}><label style={S.label}>Reason *</label><textarea style={{...S.input,height:80,resize:"none"}} placeholder="Explain what needs to be fixed…" value={flagNote} onChange={e=>setFlagNote(e.target.value)} /></div>
            <div style={{fontSize:12,color:"#f97316",marginBottom:16,padding:"8px 12px",background:"rgba(249,115,22,0.08)",borderRadius:8}}>⚠ This will send the whole trip back to the employee for correction.</div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...S.btn,...S.btnWarn}} onClick={flagExpense}>Flag & Send Back</button>
              <button style={{...S.btn,...S.btnGhost}} onClick={()=>setFlagModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Expense List ─────────────────────────────────────────────────────────────
function ExpenseList({user,expenses,onSelect}) {
  const S=useS(); const t=S.t;
  const [company,setCompany]=useState("all"); const [status,setStatus]=useState("all"); const [search,setSearch]=useState("");
  const base=user.role==="employee"?expenses.filter(e=>e.employeeId===user.id):expenses;
  const visible=base.filter(e=>{
    if(company!=="all"&&e.company!==company)return false;
    if(status!=="all"&&e.status!==status)return false;
    if(search&&!`${e.description}${e.employeeName}${e.category}${e.currency}`.toLowerCase().includes(search.toLowerCase()))return false;
    return true;
  });
  const totalUSD=visible.reduce((s,e)=>s+(e.amountUSD||0),0);
  return (
    <div>
      <h2 style={S.h2}>{user.role==="employee"?"My Expenses":"All Expenses"}</h2>
      <p style={S.sub}>Individual expenses linked to trips.</p>
      {user.role!=="employee"&&(
        <div style={{display:"flex",gap:6,marginBottom:14}}>
          <button onClick={()=>setCompany("all")} style={{...S.btn,...(company==="all"?S.btnPrimary:S.btnGhost),padding:"7px 16px",fontSize:13}}>All <span style={{opacity:.6,marginLeft:4}}>{base.length}</span></button>
          {COMPANIES.map(co=>{const active=company===co.id;return <button key={co.id} onClick={()=>setCompany(co.id)} style={{...S.btn,padding:"7px 16px",fontSize:13,background:active?co.bg:"rgba(255,255,255,0.05)",color:active?co.color:t.subText,border:`1px solid ${active?co.color+"50":"transparent"}`}}>{co.name}</button>;})}
        </div>
      )}
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <input style={{...S.input,width:200,padding:"7px 14px"}} placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} />
        {["all","pending","approved_by_m1","approved","rejected","paid"].map(f=>(
          <button key={f} style={{...S.btn,...(status===f?S.btnPrimary:S.btnGhost),padding:"6px 14px",fontSize:12}} onClick={()=>setStatus(f)}>{f==="all"?"All":STATUS_META[f]?.label||f}</button>
        ))}
        {visible.length>0&&<div style={{marginLeft:"auto",fontSize:13,color:t.subText}}>{visible.length} · <span style={{color:"#22d3ee",fontWeight:700}}>{fmtUSD(totalUSD)}</span></div>}
      </div>
      <div style={{...S.card,padding:0,overflow:"hidden"}}>
        <table style={S.table}>
          <thead><tr style={{background:t.inputBg}}>{["Company","Employee","Category","Description","Original","≈ USD","Date","Status",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {visible.length===0&&<tr><td colSpan={9} style={{...S.td,color:t.subText,textAlign:"center",padding:36}}>No expenses found.</td></tr>}
            {visible.map(e=>{
              const flag=CURRENCIES.find(c=>c.code===e.currency)?.flag||"";
              return (
                <tr key={e.id} style={{cursor:"pointer"}} onClick={()=>onSelect(e)}>
                  <td style={S.td}><CompanyTag companyId={e.company} /></td>
                  <td style={S.td}><span style={{fontWeight:600,color:t.text}}>{e.employeeName}</span>{e.tripId&&<div style={{fontSize:10,color:"#a78bfa"}}>✈ Trip</div>}</td>
                  <td style={S.td}><span style={{color:t.subText,fontSize:12}}>{e.category}</span></td>
                  <td style={S.td}><span style={{color:t.subText,maxWidth:160,display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.description}</span></td>
                  <td style={S.td}><span style={{color:t.subText}}>{flag} {fmt(e.amount)} {e.currency}</span></td>
                  <td style={S.td}><span style={{fontWeight:700,color:"#22d3ee"}}>{fmtUSD(e.amountUSD)}</span></td>
                  <td style={S.td}><span style={{color:t.subText,fontSize:12}}>{e.date}</span></td>
                  <td style={S.td}><Badge status={e.flagged?"flagged":e.status} /></td>
                  <td style={S.td}><span style={{color:t.subText}}>›</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Expense Detail Modal ─────────────────────────────────────────────────────
function ExpenseDetail({exp,user,users,settings,onClose,onUpdate,onPushZoho}) {
  const S=useS(); const t=S.t;
  const [comment,setComment]=useState(""); const [saving,setSaving]=useState(false);
  const companySetting=settings.find(s=>s.company===exp.company)||{};
  const settingsConfigured=!!(companySetting.approver1Id);
  const isApprover1=settingsConfigured?user.id===companySetting.approver1Id:user.role==="manager1";
  const isApprover2=settingsConfigured&&companySetting.approver2Id?user.id===companySetting.approver2Id:user.role==="manager2";
  const canM1=isApprover1&&exp.status==="pending"&&!exp.tripId;
  const canM2=companySetting.approver2Id?(isApprover2&&exp.status==="approved_by_m1"&&!exp.tripId):(user.role==="admin"&&exp.status==="approved_by_m1"&&!exp.tripId);
  const canRej=(isApprover1&&exp.status==="pending"||isApprover2&&exp.status==="approved_by_m1")&&!exp.tripId;
  const canPay=user.role==="admin"&&exp.status==="approved"&&!exp.tripId;
  const canZoho=user.role==="admin"&&(exp.status==="approved"||exp.status==="paid")&&!exp.pushedToZoho;
  const a1Name=users.find(u=>u.id===companySetting.approver1Id)?.name||"Approver 1";
  const a2Name=users.find(u=>u.id===companySetting.approver2Id)?.name||"Approver 2";
  const m1n=users.find(u=>u.id===exp.m1ApprovedBy)?.name;
  const m2n=users.find(u=>u.id===exp.m2ApprovedBy)?.name;
  const flag=CURRENCIES.find(c=>c.code===exp.currency)?.flag||"";
  const co=companyById(exp.company);
  const act=async type=>{
    setSaving(true);
    let updated={...exp};
    if(type==="m1"){updated.status="approved_by_m1";updated.m1ApprovedBy=user.id;updated.m1Comment=comment;}
    if(type==="m2"){updated.status="approved";updated.m2ApprovedBy=user.id;updated.m2Comment=comment;}
    if(type==="rej"){updated.status="rejected";if(isApprover1)updated.m1Comment=comment;else updated.m2Comment=comment;}
    if(type==="pay"){updated.paid=true;updated.status="paid";}
    const {error}=await supabase.from("expenses").update(expToDb(updated)).eq("id",updated.id);
    setSaving(false);
    if(!error){onUpdate(updated);setComment("");if(type==="pay")onClose();}
  };
  const Step=({num,label,done,active,by,comment:c})=>(
    <div style={{display:"flex",gap:14,marginBottom:14,alignItems:"flex-start"}}>
      <div style={{width:28,height:28,borderRadius:"50%",background:done?"#10b981":active?"#22d3ee":t.inputBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:done||active?"#fff":t.subText,flexShrink:0,marginTop:2}}>{done?"✓":num}</div>
      <div><div style={{fontSize:13,fontWeight:600,color:t.text}}>{label}</div>
        {by&&<div style={{fontSize:12,color:"#10b981",marginTop:2}}>✓ {by}{c?` — "${c}"`:""}</div>}
        {!by&&<div style={{fontSize:12,color:active?"#22d3ee":t.subText,marginTop:2}}>{active?"Awaiting approval…":"Not yet reached"}</div>}
      </div>
    </div>
  );
  return (
    <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.modalBox}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:t.text}}>{exp.description}</div>
            <div style={{display:"flex",gap:8,alignItems:"center",marginTop:6}}><CompanyTag companyId={exp.company} size="lg" /><span style={{fontSize:13,color:t.subText}}>{exp.employeeName} · {exp.date}</span>{exp.tripId&&<span style={{fontSize:11,color:"#a78bfa",background:"rgba(167,139,250,0.1)",padding:"2px 8px",borderRadius:8}}>✈ Part of a trip</span>}</div>
          </div>
          <button style={{background:"none",border:"none",color:t.subText,cursor:"pointer",fontSize:20}} onClick={onClose}>✕</button>
        </div>
        <div style={{background:`linear-gradient(135deg,${co.color}08,rgba(99,102,241,0.06))`,border:`1px solid ${co.color}20`,borderRadius:12,padding:20,marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div><div style={{fontSize:11,color:t.subText,fontWeight:700,textTransform:"uppercase",letterSpacing:".5px",marginBottom:6}}>Original</div><div style={{fontSize:22,color:t.text,fontWeight:800}}>{flag} {fmt(exp.amount)} {exp.currency}</div>{exp.rateUsed&&exp.currency!=="USD"&&<div style={{fontSize:11.5,color:t.subText,marginTop:4}}>Rate: 1 {exp.currency} = {fmtUSD(1/exp.rateUsed)}</div>}</div>
            <div style={{textAlign:"right"}}><div style={{fontSize:11,color:t.subText,fontWeight:700,textTransform:"uppercase",letterSpacing:".5px",marginBottom:6}}>To Pay (USD)</div><div style={{fontSize:30,color:"#22d3ee",fontWeight:800,letterSpacing:"-1px"}}>{fmtUSD(exp.amountUSD)}</div></div>
          </div>
        </div>
        {exp.flagged&&<div style={{marginBottom:16,padding:"10px 14px",background:"rgba(249,115,22,0.08)",borderRadius:8,fontSize:13,color:"#f97316",border:"1px solid rgba(249,115,22,0.25)"}}>⚠ Flagged: {exp.flagNote}</div>}
        {exp.receiptUrl&&(
          <div style={{marginBottom:16,padding:16,background:t.inputBg,borderRadius:10}}>
            <div style={{fontSize:12,fontWeight:700,color:t.subText,marginBottom:10,textTransform:"uppercase",letterSpacing:".5px"}}>Receipt</div>
            {exp.receiptUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)?<img src={exp.receiptUrl} alt="receipt" style={{maxWidth:"100%",maxHeight:300,borderRadius:8}} />:<a href={exp.receiptUrl} target="_blank" rel="noreferrer" style={{color:"#22d3ee",fontSize:13,fontWeight:600}}>📄 View Receipt PDF →</a>}
          </div>
        )}
        {exp.pushedToZoho&&<div style={{marginBottom:16,padding:"8px 14px",background:"rgba(99,102,241,0.08)",borderRadius:8,fontSize:12,color:"#818cf8"}}>✓ Pushed to Zoho Books</div>}
        {exp.tripId&&<div style={{marginBottom:16,padding:"8px 14px",background:"rgba(167,139,250,0.08)",borderRadius:8,fontSize:12,color:"#a78bfa"}}>✈ This expense is part of a trip. Approve the trip to process it.</div>}
        {!exp.tripId&&(
          <div style={{background:t.inputBg,borderRadius:10,padding:18,marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:t.subText,marginBottom:14,textTransform:"uppercase",letterSpacing:".5px"}}>Approval Flow</div>
            <Step num={1} label={a1Name} done={!!exp.m1ApprovedBy&&exp.status!=="rejected"} active={exp.status==="pending"} by={m1n} comment={exp.m1Comment} />
            <Step num={2} label={a2Name} done={!!exp.m2ApprovedBy} active={exp.status==="approved_by_m1"} by={m2n} comment={exp.m2Comment} />
            <Step num={3} label="Finance — Pay in USD" done={exp.paid} active={exp.status==="approved"} by={exp.paid?"Admin Finance":null} />
          </div>
        )}
        {(canM1||canM2||canRej)&&<div style={{marginBottom:18}}><label style={S.label}>Comment (optional)</label><textarea style={{...S.input,height:56,resize:"none"}} placeholder="Add a note…" value={comment} onChange={e=>setComment(e.target.value)} /></div>}
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {canM1&&<button style={{...S.btn,...S.btnSuccess,opacity:saving?0.7:1}} onClick={()=>act("m1")}>✓ Approve (M1)</button>}
          {canM2&&<button style={{...S.btn,...S.btnSuccess,opacity:saving?0.7:1}} onClick={()=>act("m2")}>✓ Approve (M2)</button>}
          {canRej&&<button style={{...S.btn,...S.btnDanger,opacity:saving?0.7:1}} onClick={()=>act("rej")}>✕ Reject</button>}
          {canPay&&<button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={()=>act("pay")}>💳 Pay {fmtUSD(exp.amountUSD)}</button>}
          {canZoho&&<button style={{...S.btn,...S.btnZoho}} onClick={()=>{onPushZoho(exp);onClose();}}>⬆ Push to Zoho</button>}
        </div>
      </div>
    </div>
  );
}

// ─── User Management ──────────────────────────────────────────────────────────
function UserManagement({users,onUpdateUsers}) {
  const S=useS(); const t=S.t;
  const [search,setSearch]=useState(""); const [filterCo,setFilterCo]=useState("all"); const [filterRole,setFilterRole]=useState("all");
  const [modal,setModal]=useState(null); const [form,setForm]=useState({}); const [inviteUser,setInviteUser]=useState(null);
  const [copied,setCopied]=useState(false); const [saving,setSaving]=useState(false);
  const visible=users.filter(u=>{
    if(filterCo!=="all"&&u.company!==filterCo)return false;
    if(filterRole!=="all"&&u.role!==filterRole)return false;
    if(search&&!`${u.name}${u.email}${u.department}${u.phone}`.toLowerCase().includes(search.toLowerCase()))return false;
    return true;
  });
  const openAdd=()=>{const p=genPwd();setForm({id:genId(),name:"",email:"",phone:"",department:"Sales",role:"employee",company:"synopsis",password:p,active:true,mustChangePwd:true,invited:false});setModal({mode:"add"});};
  const openEdit=u=>{setForm({...u});setModal({mode:"edit",user:u});};
  const openInvite=u=>{setInviteUser(u);setModal({mode:"invite",user:u});};
  const set=k=>v=>setForm(f=>({...f,[k]:v}));
  const saveUser=async()=>{
    if(!form.name||!form.email)return;
    setSaving(true);
    if(modal.mode==="add"){
      // Create Supabase Auth account first
      const {data:authData,error:authErr}=await supabase.auth.admin.createUser({
        email:form.email,
        password:form.password,
        email_confirm:true,
      });
      if(!authErr&&authData?.user){
        const newUser={...form,auth_id:authData.user.id};
        const {error}=await supabase.from("users").insert(userToDb({...newUser,authId:authData.user.id}));
        if(!error) onUpdateUsers([...users,{...newUser,authId:authData.user.id}]);
      } else {
        // If admin API not available, insert user without auth_id for now
        const {error}=await supabase.from("users").insert(userToDb(form));
        if(!error) onUpdateUsers([...users,form]);
      }
    } else {
      const {error}=await supabase.from("users").update(userToDb(form)).eq("id",form.id);
      if(!error) onUpdateUsers(users.map(u=>u.id===form.id?form:u));
    }
    setSaving(false); setModal(null);
  };
  const toggleActive=async u=>{const {error}=await supabase.from("users").update({active:!u.active}).eq("id",u.id);if(!error)onUpdateUsers(users.map(x=>x.id===u.id?{...x,active:!x.active}:x));};
  const inviteLink=u=>{const base=window.location.href.split("?")[0];return `${base}?email=${encodeURIComponent(u.email)}`;};
  const copyLink=async u=>{navigator.clipboard.writeText(inviteLink(u));setCopied(true);setTimeout(()=>setCopied(false),2500);await supabase.from("users").update({invited:true}).eq("id",u.id);onUpdateUsers(users.map(x=>x.id===u.id?{...x,invited:true}:x));};
  const stats={total:users.length,active:users.filter(u=>u.active).length,invited:users.filter(u=>u.invited).length,mustChange:users.filter(u=>u.mustChangePwd).length};
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}><h2 style={S.h2}>User Management</h2><button style={{...S.btn,...S.btnPrimary}} onClick={openAdd}>+ Add Employee</button></div>
      <p style={S.sub}>Manage your team across both companies.</p>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:20}}>
        {[{l:"Total",v:stats.total,c:"#22d3ee"},{l:"Active",v:stats.active,c:"#10b981"},{l:"Invited",v:stats.invited,c:"#a78bfa"},{l:"Must Change Pwd",v:stats.mustChange,c:"#f59e0b"}].map(s=>(
          <div key={s.l} style={S.statCard}><div style={{fontSize:24,fontWeight:800,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:t.subText,marginTop:4}}>{s.l}</div></div>
        ))}
      </div>
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <input style={{...S.input,width:220,padding:"8px 14px"}} placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} />
        <select style={{...S.input,width:180}} value={filterCo} onChange={e=>setFilterCo(e.target.value)}><option value="all">All Companies</option>{COMPANIES.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <select style={{...S.input,width:150}} value={filterRole} onChange={e=>setFilterRole(e.target.value)}><option value="all">All Roles</option>{ROLES.map(r=><option key={r} value={r}>{r}</option>)}</select>
        <span style={{marginLeft:"auto",fontSize:13,color:t.subText}}>{visible.length}/{users.length}</span>
      </div>
      <div style={{...S.card,padding:0,overflow:"hidden"}}>
        <table style={S.table}>
          <thead><tr style={{background:t.inputBg}}>{["","Employee","Dept","Company","Role","Status","Invited","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {visible.map(u=>(
              <tr key={u.id} style={{opacity:u.active?1:0.5}}>
                <td style={{...S.td,width:44,paddingRight:0}}><Avatar name={u.name} size={32} /></td>
                <td style={S.td}><div style={{fontWeight:600,color:t.text}}>{u.name}</div><div style={{fontSize:12,color:t.subText}}>{u.email}</div>{u.phone&&<div style={{fontSize:11,color:t.subText,opacity:.6}}>{u.phone}</div>}</td>
                <td style={S.td}><span style={{color:t.subText,fontSize:13}}>{u.department}</span></td>
                <td style={S.td}><CompanyTag companyId={u.company} /></td>
                <td style={S.td}><RolePill role={u.role} /></td>
                <td style={S.td}>{u.active?<span style={{color:"#10b981",fontSize:12,fontWeight:600}}>● Active</span>:<span style={{color:"#ef4444",fontSize:12,fontWeight:600}}>● Inactive</span>}{u.mustChangePwd&&<div style={{fontSize:10,color:"#f59e0b",marginTop:2}}>⚠ pwd pending</div>}</td>
                <td style={S.td}>{u.invited?<span style={{color:"#10b981",fontSize:12}}>✓ Sent</span>:<span style={{color:t.subText,fontSize:12}}>Not sent</span>}</td>
                <td style={S.td}><div style={{display:"flex",gap:6}}><button style={{...S.btn,...S.btnGhost,padding:"5px 10px",fontSize:11.5}} onClick={()=>openEdit(u)}>Edit</button><button style={{...S.btn,...S.btnZoho,padding:"5px 10px",fontSize:11.5}} onClick={()=>openInvite(u)}>🔗 Invite</button><button style={{...S.btn,...(u.active?S.btnDanger:S.btnSuccess),padding:"5px 10px",fontSize:11.5}} onClick={()=>toggleActive(u)}>{u.active?"Deactivate":"Activate"}</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(modal?.mode==="add"||modal?.mode==="edit")&&(
        <div style={S.modal} onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div style={S.modalBox}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}><div style={{fontSize:17,fontWeight:800,color:t.text}}>{modal.mode==="add"?"Add Employee":"Edit Employee"}</div><button style={{background:"none",border:"none",color:t.subText,cursor:"pointer",fontSize:20}} onClick={()=>setModal(null)}>✕</button></div>
            <div style={S.grid2}>
              {[["Full Name *","name","text","Jane Doe"],["Email *","email","email","jane@company.com"],["Phone","phone","text","+212 600 000 000"]].map(([lbl,k,ty,ph])=>(
                <div key={k} style={{marginBottom:16}}><label style={S.label}>{lbl}</label><input style={S.input} type={ty} value={form[k]||""} onChange={e=>set(k)(e.target.value)} placeholder={ph} /></div>
              ))}
              <div style={{marginBottom:16}}><label style={S.label}>Department</label><select style={S.input} value={form.department||"Sales"} onChange={e=>set("department")(e.target.value)}>{DEPARTMENTS.map(d=><option key={d}>{d}</option>)}</select></div>
              <div style={{marginBottom:16}}><label style={S.label}>Role</label><select style={S.input} value={form.role||"employee"} onChange={e=>set("role")(e.target.value)}>{ROLES.map(r=><option key={r} value={r}>{r}</option>)}</select></div>
              <div style={{marginBottom:16}}><label style={S.label}>Company</label><select style={S.input} value={form.company||"synopsis"} onChange={e=>set("company")(e.target.value)}>{COMPANIES.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            </div>
            {modal.mode==="add"&&(<div style={{marginBottom:20,padding:16,background:t.inputBg,borderRadius:10,border:`1px solid ${t.border}`}}><div style={{fontSize:12,color:t.subText,fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:".5px"}}>Temporary Password</div><div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}><code style={{fontSize:16,fontWeight:800,color:"#f59e0b",letterSpacing:"2px"}}>{form.password}</code><button style={{...S.btn,...S.btnGhost,fontSize:11,padding:"4px 10px"}} onClick={()=>set("password")(genPwd())}>Regenerate</button></div><div style={{fontSize:11.5,color:t.subText,marginTop:8}}>Employee must change on first login.</div></div>)}
            <div style={{display:"flex",gap:10}}><button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={saveUser}>{saving?"Saving…":modal.mode==="add"?"Create Account":"Save Changes"}</button><button style={{...S.btn,...S.btnGhost}} onClick={()=>setModal(null)}>Cancel</button></div>
          </div>
        </div>
      )}
      {modal?.mode==="invite"&&inviteUser&&(
        <div style={S.modal} onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div style={S.modalSm}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}><div style={{fontSize:17,fontWeight:800,color:t.text}}>Send Access Link</div><button style={{background:"none",border:"none",color:t.subText,cursor:"pointer",fontSize:20}} onClick={()=>setModal(null)}>✕</button></div>
            <div style={{display:"flex",alignItems:"center",gap:14,padding:16,background:t.inputBg,borderRadius:12,marginBottom:20}}><Avatar name={inviteUser.name} size={44} /><div><div style={{fontSize:14,fontWeight:700,color:t.text}}>{inviteUser.name}</div><div style={{fontSize:13,color:t.subText}}>{inviteUser.email}</div><div style={{marginTop:6}}><CompanyTag companyId={inviteUser.company} /></div></div></div>
            <div style={{marginBottom:16}}><label style={S.label}>Access Link</label><div style={{display:"flex",gap:8}}><input style={{...S.input,fontSize:11.5,flex:1}} value={inviteLink(inviteUser)} readOnly /><button style={{...S.btn,...S.btnPrimary,flexShrink:0}} onClick={()=>copyLink(inviteUser)}>{copied?"✓ Copied!":"Copy"}</button></div></div>
            <div style={{padding:16,background:t.inputBg,borderRadius:10,marginBottom:20}}><div style={{fontSize:12,color:t.subText,fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:".5px"}}>Credentials</div><div style={{marginBottom:6}}><span style={{color:t.subText,fontSize:13}}>Email: </span><span style={{color:t.text,fontSize:13}}>{inviteUser.email}</span></div><div><span style={{color:t.subText,fontSize:13}}>Password: </span><code style={{color:"#f59e0b",fontWeight:800,fontSize:14}}>{inviteUser.password}</code></div>{inviteUser.mustChangePwd&&<div style={{fontSize:11.5,color:"#f59e0b",marginTop:8}}>⚠ Must change on first login.</div>}</div>
            <button style={{...S.btn,...S.btnPrimary,width:"100%"}} onClick={()=>{copyLink(inviteUser);setModal(null);}}>Copy Link & Mark as Invited ✓</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Category Management ──────────────────────────────────────────────────────
function CategoryManagement({categories,onUpdateCategories}) {
  const S=useS(); const t=S.t;
  const [newCat,setNewCat]=useState(""); const [saving,setSaving]=useState(false); const [err,setErr]=useState("");
  const addCategory=async()=>{
    const name=newCat.trim();
    if(!name){setErr("Please enter a category name.");return;}
    if(categories.find(c=>c.name.toLowerCase()===name.toLowerCase())){setErr("Category already exists.");return;}
    setSaving(true); setErr("");
    const id="c"+Date.now();
    const {error}=await supabase.from("categories").insert({id,name,active:true});
    setSaving(false);
    if(!error){onUpdateCategories([...categories,{id,name,active:true}]);setNewCat("");}
    else setErr("Error saving. Try again.");
  };
  const toggleCategory=async cat=>{const {error}=await supabase.from("categories").update({active:!cat.active}).eq("id",cat.id);if(!error)onUpdateCategories(categories.map(c=>c.id===cat.id?{...c,active:!c.active}:c));};
  const deleteCategory=async cat=>{const {error}=await supabase.from("categories").delete().eq("id",cat.id);if(!error)onUpdateCategories(categories.filter(c=>c.id!==cat.id));};
  return (
    <div>
      <h2 style={S.h2}>Expense Categories</h2>
      <p style={S.sub}>Add, disable, or remove categories. Changes apply immediately.</p>
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:18}}>➕ Add New Category</div>
          <div style={{marginBottom:14}}><label style={S.label}>Category Name</label><input style={S.input} placeholder="e.g. Entertainment, Parking…" value={newCat} onChange={e=>setNewCat(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addCategory()} /></div>
          {err&&<div style={{color:"#ef4444",fontSize:13,marginBottom:12,background:"rgba(239,68,68,0.08)",padding:"10px 14px",borderRadius:8}}>{err}</div>}
          <button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={addCategory}>{saving?"Saving…":"Add Category"}</button>
        </div>
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:18}}>📋 All Categories ({categories.length})</div>
          {categories.length===0&&<div style={{color:t.subText,fontSize:13}}>No categories yet.</div>}
          {categories.map(cat=>(
            <div key={cat.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${t.border}`,opacity:cat.active?1:0.5}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}><div style={{width:8,height:8,borderRadius:"50%",background:cat.active?"#10b981":t.subText}} /><span style={{fontSize:13,color:t.text,fontWeight:500}}>{cat.name}</span>{!cat.active&&<span style={{fontSize:11,color:t.subText}}>(disabled)</span>}</div>
              <div style={{display:"flex",gap:6}}>
                <button style={{...S.btn,...(cat.active?S.btnDanger:S.btnSuccess),padding:"4px 10px",fontSize:11.5}} onClick={()=>toggleCategory(cat)}>{cat.active?"Disable":"Enable"}</button>
                <button style={{...S.btn,...S.btnDanger,padding:"4px 10px",fontSize:11.5}} onClick={()=>deleteCategory(cat)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Spend Policy ─────────────────────────────────────────────────────────────
function SpendPolicyPage({categories,policies,onUpdatePolicies}) {
  const S=useS(); const t=S.t;
  const [saving,setSaving]=useState(false); const [saved,setSaved]=useState(false);
  const [form,setForm]=useState(()=>{
    const f={};
    COMPANIES.forEach(co=>{
      categories.forEach(cat=>{
        const p=policies.find(p=>p.company===co.id&&p.category===cat);
        f[`${co.id}__${cat}`]=p?String(p.maxUsd):"";
      });
    });
    return f;
  });

  useEffect(()=>{
    const f={};
    COMPANIES.forEach(co=>{
      categories.forEach(cat=>{
        const p=policies.find(p=>p.company===co.id&&p.category===cat);
        f[`${co.id}__${cat}`]=p?String(p.maxUsd):"";
      });
    });
    setForm(f);
  },[policies,categories]);

  const save=async()=>{
    setSaving(true);
    await supabase.from("policies").delete().neq("id","x");
    const rows=[];
    Object.entries(form).forEach(([key,val])=>{
      if(val&&parseFloat(val)>0){
        const [company,category]=key.split("__");
        rows.push({id:`p${company}${category}`.replace(/[^a-z0-9]/gi,""),company,category,max_usd:parseFloat(val)});
      }
    });
    if(rows.length>0) await supabase.from("policies").insert(rows);
    onUpdatePolicies(rows.map(r=>({company:r.company,category:r.category,maxUsd:r.max_usd})));
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2500);
  };

  return (
    <div>
      <h2 style={S.h2}>💰 Spend Policies</h2>
      <p style={S.sub}>Set maximum USD amounts per category per company. Leave blank for no limit. Employees will be warned when exceeded.</p>
      {COMPANIES.map(co=>(
        <div key={co.id} style={{...S.card,border:`1px solid ${co.color}25`}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}><CompanyTag companyId={co.id} size="lg" /><span style={{fontSize:13,color:t.subText}}>Max amount per expense (USD)</span></div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
            {categories.map(cat=>(
              <div key={cat}>
                <label style={S.label}>{cat}</label>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{color:t.subText,fontSize:13}}>$</span>
                  <input style={{...S.input,flex:1}} type="number" placeholder="No limit" value={form[`${co.id}__${cat}`]||""} onChange={e=>setForm(f=>({...f,[`${co.id}__${cat}`]:e.target.value}))} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        {saved&&<div style={{color:"#10b981",fontSize:13,background:"rgba(16,185,129,0.08)",padding:"10px 16px",borderRadius:8,border:"1px solid rgba(16,185,129,0.2)"}}>✓ Policies saved!</div>}
        <button style={{...S.btn,...S.btnPrimary,padding:"11px 28px",opacity:saving?0.7:1}} onClick={save}>{saving?"Saving…":"Save Policies"}</button>
      </div>
    </div>
  );
}

// ─── Approver Settings ────────────────────────────────────────────────────────
function ApproverSettings({users,settings,onUpdateSettings}) {
  const S=useS(); const t=S.t;
  const [form,setForm]=useState({
    synopsis_a1:  settings.find(s=>s.company==="synopsis")?.approver1Id||"",
    synopsis_a2:  settings.find(s=>s.company==="synopsis")?.approver2Id||"",
    synopsis_a3:  settings.find(s=>s.company==="synopsis")?.approver3Id||"",
    synopsis_count: String(settings.find(s=>s.company==="synopsis")?.approverCount||2),
    incloudites_a1: settings.find(s=>s.company==="incloudites")?.approver1Id||"",
    incloudites_a2: settings.find(s=>s.company==="incloudites")?.approver2Id||"",
    incloudites_a3: settings.find(s=>s.company==="incloudites")?.approver3Id||"",
    incloudites_count: String(settings.find(s=>s.company==="incloudites")?.approverCount||2),
  });
  const [saved,setSaved]=useState(false); const [saving,setSaving]=useState(false);

  useEffect(()=>{
    setForm({
      synopsis_a1:  settings.find(s=>s.company==="synopsis")?.approver1Id||"",
      synopsis_a2:  settings.find(s=>s.company==="synopsis")?.approver2Id||"",
      synopsis_a3:  settings.find(s=>s.company==="synopsis")?.approver3Id||"",
      synopsis_count: String(settings.find(s=>s.company==="synopsis")?.approverCount||2),
      incloudites_a1: settings.find(s=>s.company==="incloudites")?.approver1Id||"",
      incloudites_a2: settings.find(s=>s.company==="incloudites")?.approver2Id||"",
      incloudites_a3: settings.find(s=>s.company==="incloudites")?.approver3Id||"",
      incloudites_count: String(settings.find(s=>s.company==="incloudites")?.approverCount||2),
    });
  },[settings]);

  const save=async()=>{
    setSaving(true);
    await supabase.from("settings").update({approver1_id:form.synopsis_a1||null,approver2_id:form.synopsis_a2||null,approver3_id:form.synopsis_a3||null,approver_count:parseInt(form.synopsis_count)||2}).eq("company","synopsis");
    await supabase.from("settings").update({approver1_id:form.incloudites_a1||null,approver2_id:form.incloudites_a2||null,approver3_id:form.incloudites_a3||null,approver_count:parseInt(form.incloudites_count)||2}).eq("company","incloudites");
    setSaving(false);
    onUpdateSettings([
      {company:"synopsis",    approver1Id:form.synopsis_a1,    approver2Id:form.synopsis_a2,    approver3Id:form.synopsis_a3,    approverCount:parseInt(form.synopsis_count)||2},
      {company:"incloudites", approver1Id:form.incloudites_a1, approver2Id:form.incloudites_a2, approver3Id:form.incloudites_a3, approverCount:parseInt(form.incloudites_count)||2},
    ]);
    setSaved(true); setTimeout(()=>setSaved(false),2500);
  };

  const ApproverCard=({companyId,a1Key,a2Key,a3Key,countKey})=>{
    const co=companyById(companyId);
    const eligible=users.filter(u=>u.active&&u.company===companyId);
    const count=parseInt(form[countKey])||2;
    const a1=users.find(u=>u.id===form[a1Key]);
    const a2=users.find(u=>u.id===form[a2Key]);
    const a3=users.find(u=>u.id===form[a3Key]);
    return (
      <div style={{...S.card,border:`1px solid ${co.color}25`}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}><CompanyTag companyId={companyId} size="lg" /><span style={{fontSize:13,color:t.subText}}>Approval Chain</span></div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,color:t.subText}}>Approvers:</span>
            {[1,2,3].map(n=>(
              <button key={n} style={{...S.btn,padding:"4px 10px",fontSize:12,...(count===n?S.btnPrimary:S.btnGhost)}} onClick={()=>setForm(f=>({...f,[countKey]:String(n)}))}>
                {n}
              </button>
            ))}
          </div>
        </div>
        {/* Step 1 */}
        <div style={{marginBottom:12,padding:16,background:t.inputBg,borderRadius:10,border:`1px solid ${t.border}`}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
            <div style={{width:24,height:24,borderRadius:"50%",background:"#22d3ee",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#0b0e17"}}>1</div>
            <div style={{fontSize:13,fontWeight:700,color:t.text}}>First Approver</div>
          </div>
          <select style={S.input} value={form[a1Key]} onChange={e=>setForm(f=>({...f,[a1Key]:e.target.value}))}>
            <option value="">— Select approver —</option>
            {eligible.map(u=><option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
          {a1&&<div style={{marginTop:8,display:"flex",alignItems:"center",gap:8}}><Avatar name={a1.name} size={24} /><span style={{fontSize:12,color:"#10b981"}}>✓ {a1.name} · {a1.department}</span></div>}
        </div>
        <div style={{textAlign:"center",marginBottom:12,fontSize:18,color:t.subText}}>↓</div>
        {/* Step 2 */}
        {count>=2&&(
          <>
            <div style={{marginBottom:12,padding:16,background:t.inputBg,borderRadius:10,border:`1px solid ${t.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <div style={{width:24,height:24,borderRadius:"50%",background:"#a78bfa",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#0b0e17"}}>2</div>
                <div style={{fontSize:13,fontWeight:700,color:t.text}}>Second Approver</div>
              </div>
              <select style={S.input} value={form[a2Key]} onChange={e=>setForm(f=>({...f,[a2Key]:e.target.value}))}>
                <option value="">— Select approver —</option>
                {eligible.map(u=><option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
              {a2&&<div style={{marginTop:8,display:"flex",alignItems:"center",gap:8}}><Avatar name={a2.name} size={24} /><span style={{fontSize:12,color:"#10b981"}}>✓ {a2.name} · {a2.department}</span></div>}
            </div>
            <div style={{textAlign:"center",marginBottom:12,fontSize:18,color:t.subText}}>↓</div>
          </>
        )}
        {/* Step 3 (optional) */}
        {count===3&&(
          <>
            <div style={{marginBottom:12,padding:16,background:t.inputBg,borderRadius:10,border:`1px solid ${t.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <div style={{width:24,height:24,borderRadius:"50%",background:"#10b981",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#0b0e17"}}>3</div>
                <div style={{fontSize:13,fontWeight:700,color:t.text}}>Third Approver</div>
              </div>
              <select style={S.input} value={form[a3Key]} onChange={e=>setForm(f=>({...f,[a3Key]:e.target.value}))}>
                <option value="">— Select approver —</option>
                {eligible.map(u=><option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
              {a3&&<div style={{marginTop:8,display:"flex",alignItems:"center",gap:8}}><Avatar name={a3.name} size={24} /><span style={{fontSize:12,color:"#10b981"}}>✓ {a3.name} · {a3.department}</span></div>}
            </div>
            <div style={{textAlign:"center",marginBottom:12,fontSize:18,color:t.subText}}>↓</div>
          </>
        )}
        {/* Final step - always admin */}
        <div style={{padding:16,background:t.inputBg,borderRadius:10,border:`1px solid ${t.border}`,opacity:0.6}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:24,height:24,borderRadius:"50%",background:"#f59e0b",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#0b0e17"}}>{count+1}</div>
            <div><div style={{fontSize:13,fontWeight:700,color:t.text}}>Finance / Admin</div><div style={{fontSize:11.5,color:t.subText,marginTop:2}}>Fixed — marks as paid in USD</div></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <h2 style={S.h2}>⚙️ Approval Settings</h2>
      <p style={S.sub}>Set who approves expenses and trips for each company. Changes take effect immediately.</p>
      <div style={S.grid2}>
        <ApproverCard companyId="synopsis"    a1Key="synopsis_a1"    a2Key="synopsis_a2"    a3Key="synopsis_a3"    countKey="synopsis_count" />
        <ApproverCard companyId="incloudites" a1Key="incloudites_a1" a2Key="incloudites_a2" a3Key="incloudites_a3" countKey="incloudites_count" />
      </div>
      <div style={{display:"flex",alignItems:"center",gap:12,marginTop:4}}>
        {saved&&<div style={{color:"#10b981",fontSize:13,background:"rgba(16,185,129,0.08)",padding:"10px 16px",borderRadius:8,border:"1px solid rgba(16,185,129,0.2)"}}>✓ Approvers saved!</div>}
        <button style={{...S.btn,...S.btnPrimary,padding:"11px 28px",opacity:saving?0.7:1}} onClick={save}>{saving?"Saving…":"Save Approver Settings"}</button>
      </div>
    </div>
  );
}

// ─── Zoho Panel ───────────────────────────────────────────────────────────────
function ZohoPanel({expenses,onPushZoho}) {
  const S=useS(); const t=S.t;
  const [log,setLog]=useState({});
  const [pushing,setPushing]=useState(false);
  const pushable=expenses.filter(e=>(e.status==="approved"||e.status==="paid")&&!e.pushedToZoho);

  const pushExpenses=async(exps)=>{
    setPushing(true);
    exps.forEach(e=>setLog(l=>({...l,[e.id]:"pushing"})));
    try{
      // Pass clean fields needed by Edge Function
      const cleanExps=exps.map(e=>({
        id:e.id,
        company:e.company,
        date:e.date,
        amountUSD:e.amountUSD,
        amount:e.amount,
        currency:e.currency,
        description:e.description,
        vendor:e.vendor||"",
        category:e.category,
      }));
      const result=await supabase.functions.invoke("zoho-sync",{body:{expenses:cleanExps}});
      const results=result.data?.results||[];
      for(const r of results){
        if(r.success){
          // Mark as pushed in DB
          await supabase.from("expenses").update({pushed_to_zoho:true}).eq("id",r.id);
          setLog(l=>({...l,[r.id]:"done"}));
          onPushZoho(exps.find(e=>e.id===r.id));
        } else {
          setLog(l=>({...l,[r.id]:"error: "+(r.message||"failed")}));
        }
      }
    }catch(e){
      exps.forEach(ex=>setLog(l=>({...l,[ex.id]:"error"})));
    }
    setPushing(false);
  };

  const pushSingle=async(e)=>pushExpenses([e]);
  const pushAll=async(companyId)=>{
    const toPush=pushable.filter(e=>!companyId||e.company===companyId);
    if(toPush.length===0)return;
    await pushExpenses(toPush);
  };

  const exportCSV=companyId=>{
    const rows=[["ID","Company","Employee","Category","Description","Original","Currency","USD","Rate","Date","Vendor","Status","Zoho Account","Trip"]];
    expenses.filter(e=>!companyId||e.company===companyId).forEach(e=>{const co=companyById(e.company);rows.push([e.id,co.name,e.employeeName,e.category,e.description,e.amount,e.currency,(e.amountUSD||0).toFixed(2),e.rateUsed||1,e.date,e.vendor||"",e.status,co.zohoAccount,e.tripId||""]);});
    const csv=rows.map(r=>r.map(c=>`"${c}"`).join(",")).join("\n");
    const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download=`expenses_${companyId||"all"}_usd.csv`;a.click();
  };

  return (
    <div>
      <h2 style={S.h2}>Zoho Books Integration</h2>
      <p style={S.sub}>Push approved expenses directly to Zoho Books. Auto-refreshes token.</p>

      {/* Status */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:20}}>
        {[
          {l:"Ready to Push",v:pushable.length,c:"#f59e0b"},
          {l:"Already Pushed",v:expenses.filter(e=>e.pushedToZoho).length,c:"#10b981"},
          {l:"Total Approved",v:expenses.filter(e=>e.status==="approved"||e.status==="paid").length,c:"#22d3ee"},
        ].map(s=>(
          <div key={s.l} style={S.statCard}><div style={{fontSize:24,fontWeight:800,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:t.subText,marginTop:4}}>{s.l}</div></div>
        ))}
      </div>

      {/* Per company */}
      <div style={S.grid2}>
        {COMPANIES.map(co=>{
          const coPushable=pushable.filter(e=>e.company===co.id);
          return (
            <div key={co.id} style={{...S.card,border:`1px solid ${co.color}20`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <CompanyTag companyId={co.id} size="lg" />
                  <span style={{fontSize:13,color:t.subText}}>{coPushable.length} pending</span>
                </div>
                <div style={{display:"flex",gap:8}}>
                  {coPushable.length>0&&(
                    <button style={{...S.btn,...S.btnZoho,fontSize:11.5,padding:"5px 12px",opacity:pushing?0.7:1}} onClick={()=>pushExpenses(coPushable)} disabled={pushing}>
                      ⬆ Push All ({coPushable.length})
                    </button>
                  )}
                  <button style={{...S.btn,...S.btnGhost,fontSize:11.5,padding:"5px 12px"}} onClick={()=>exportCSV(co.id)}>⬇ CSV</button>
                </div>
              </div>
              {coPushable.length===0&&<div style={{color:t.subText,fontSize:13,padding:"10px 0"}}>✓ All pushed to Zoho.</div>}
              {coPushable.map(e=>{
                const flag=CURRENCIES.find(c=>c.code===e.currency)?.flag||"";
                const state=log[e.id];
                return (
                  <div key={e.id} style={{padding:"11px 0",borderBottom:`1px solid ${t.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:13,color:t.text,fontWeight:500}}>{e.description}</div>
                      <div style={{fontSize:11.5,color:t.subText}}>{e.employeeName} · {flag} {fmt(e.amount)} {e.currency} → <span style={{color:"#22d3ee",fontWeight:700}}>{fmtUSD(e.amountUSD)}</span></div>
                      <div style={{fontSize:11,color:t.subText,marginTop:2}}>{e.date} · {e.category}</div>
                    </div>
                    <div style={{flexShrink:0,marginLeft:10}}>
                      {state==="pushing"&&<span style={{fontSize:12,color:"#f59e0b"}}>⟳ Pushing…</span>}
                      {state==="done"&&<span style={{fontSize:12,color:"#10b981"}}>✓ Pushed to Zoho</span>}
                      {state&&state.startsWith("error")&&<span style={{fontSize:11,color:"#ef4444"}}>✕ {state.replace("error: ","")}</span>}
                      {!state&&<button style={{...S.btn,...S.btnZoho,fontSize:11.5,padding:"5px 12px"}} onClick={()=>pushSingle(e)}>Push →</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Already pushed */}
      {expenses.filter(e=>e.pushedToZoho).length>0&&(
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:4}}>✅ Already Pushed to Zoho ({expenses.filter(e=>e.pushedToZoho).length})</div>
          <div style={{fontSize:12,color:t.subText,marginBottom:16}}>These expenses are already in Zoho Books.</div>
          {expenses.filter(e=>e.pushedToZoho).slice(0,10).map(e=>(
            <div key={e.id} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${t.border}`}}>
              <div>
                <div style={{fontSize:13,color:t.text}}>{e.description}</div>
                <div style={{fontSize:11.5,color:t.subText}}>{e.employeeName} · {e.date}</div>
              </div>
              <span style={{fontSize:13,fontWeight:700,color:"#10b981"}}>{fmtUSD(e.amountUSD)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{textAlign:"right",marginTop:8}}>
        <button style={{...S.btn,...S.btnGhost}} onClick={()=>exportCSV(null)}>⬇ Export All CSV</button>
      </div>
    </div>
  );
}

// ─── Admin Settings Page ──────────────────────────────────────────────────────
function AdminSettingsPage({appConfig,onSaveConfig,users,budgets,onSaveBudgets}) {
  const S=useS(); const t=S.t;
  const [tab,setTab]=useState("notifications");
  const [cfg,setCfg]=useState(appConfig);
  const [saved,setSaved]=useState(false); const [saving,setSaving]=useState(false);
  const [budgetForm,setBudgetForm]=useState(()=>{
    const f={};
    COMPANIES.forEach(co=>DEPARTMENTS.forEach(dep=>{
      const b=budgets.find(b=>b.company===co.id&&b.department===dep);
      f[`${co.id}__${dep}`]=b?String(b.budgetUsd):"";
    }));
    return f;
  });

  const saveConfig=async()=>{
    setSaving(true);
    await supabase.from("app_settings").upsert({key:"app_config",value:cfg});
    onSaveConfig(cfg); setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2500);
  };

  const saveBudgets=async()=>{
    setSaving(true);
    const rows=[];
    const now=new Date();
    const quarter=`Q${Math.ceil((now.getMonth()+1)/3)}`;
    const year=now.getFullYear();
    Object.entries(budgetForm).forEach(([key,val])=>{
      if(val&&parseFloat(val)>0){
        const [company,department]=key.split("__");
        rows.push({id:`b${company}${department}${quarter}${year}`.replace(/[^a-z0-9]/gi,""),company,department,quarter,year,budget_usd:parseFloat(val)});
      }
    });
    await supabase.from("department_budgets").upsert(rows);
    onSaveBudgets(rows.map(r=>({company:r.company,department:r.department,quarter:r.quarter,year:r.year,budgetUsd:r.budget_usd})));
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2500);
  };

  const Toggle=({label,desc,field})=>(
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"16px 0",borderBottom:`1px solid ${t.border}`}}>
      <div><div style={{fontSize:13,fontWeight:600,color:t.text}}>{label}</div>{desc&&<div style={{fontSize:12,color:t.subText,marginTop:3}}>{desc}</div>}</div>
      <div style={{flexShrink:0,marginLeft:20,cursor:"pointer"}} onClick={()=>setCfg(c=>({...c,[field]:!c[field]}))}>
        <div style={{width:44,height:24,borderRadius:12,background:cfg[field]?"#22d3ee":t.border,transition:"background .2s",position:"relative"}}>
          <div style={{width:20,height:20,borderRadius:"50%",background:"#fff",position:"absolute",top:2,left:cfg[field]?22:2,transition:"left .2s"}} />
        </div>
      </div>
    </div>
  );

  const tabs=[
    {id:"notifications",icon:"🔔",label:"Notifications"},
    {id:"approval",icon:"✅",label:"Approval"},
    {id:"retention",icon:"🗄",label:"Receipts"},
    {id:"whatsapp",icon:"💬",label:"WhatsApp/SMS"},
    {id:"budgets",icon:"💰",label:"Dept. Budgets"},
  ];

  return (
    <div>
      <h2 style={S.h2}>⚙️ Admin Settings</h2>
      <p style={S.sub}>Configure notifications, approvals, retention and integrations.</p>
      <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
        {tabs.map(tb=><button key={tb.id} style={{...S.btn,...(tab===tb.id?S.btnPrimary:S.btnGhost),padding:"8px 16px",fontSize:12}} onClick={()=>setTab(tb.id)}>{tb.icon} {tb.label}</button>)}
      </div>

      {/* Notifications Tab */}
      {tab==="notifications"&&(
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:4}}>🔔 Email Notification Triggers</div>
          <div style={{fontSize:12,color:t.subText,marginBottom:16}}>Choose which events send automatic email notifications.</div>
          <Toggle label="Trip Submitted" desc="Notify Approver 1 when an employee submits a trip" field="notifyOnSubmit" />
          <Toggle label="Trip Approved (Step 1)" desc="Notify Approver 2 when step 1 is approved" field="notifyOnM1Approve" />
          <Toggle label="Trip Approved (Step 2)" desc="Notify Admin when fully approved and ready for payment" field="notifyOnM2Approve" />
          <Toggle label="Trip Rejected" desc="Notify employee when their trip is rejected" field="notifyOnReject" />
          <Toggle label="Trip Paid" desc="Notify employee and approvers when payment is confirmed" field="notifyOnPaid" />
          <Toggle label="Budget Alert" desc="Notify manager when department spend reaches 80% of budget" field="notifyOnBudgetAlert" />
          <div style={{marginTop:20,display:"flex",gap:12,alignItems:"center"}}>
            {saved&&<div style={{color:"#10b981",fontSize:13,background:"rgba(16,185,129,0.08)",padding:"8px 14px",borderRadius:8}}>✓ Saved!</div>}
            <button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={saveConfig}>{saving?"Saving…":"Save Settings"}</button>
          </div>
        </div>
      )}

      {/* Approval Tab */}
      {tab==="approval"&&(
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:4}}>✅ Approval Preferences</div>
          <div style={{fontSize:12,color:t.subText,marginBottom:16}}>Control how the approval workflow behaves.</div>
          <Toggle label="Allow approvers to approve their own trips" desc="If disabled, approvers cannot approve trips they submitted themselves" field="approverCanSelfApprove" />
          <Toggle label="Send approver a copy when trip is fully approved" desc="Approver 1 receives a copy when Approver 2 gives final approval" field="copyApproverOnFinalApproval" />
          <Toggle label="Require comment on rejection" desc="Approvers must provide a reason when rejecting a trip" field="requireCommentOnReject" />
          <Toggle label="Allow partial trip re-submission" desc="Employees can fix only flagged expenses without resubmitting the whole trip" field="allowPartialResubmit" />
          <div style={{marginTop:20,display:"flex",gap:12,alignItems:"center"}}>
            {saved&&<div style={{color:"#10b981",fontSize:13,background:"rgba(16,185,129,0.08)",padding:"8px 14px",borderRadius:8}}>✓ Saved!</div>}
            <button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={saveConfig}>{saving?"Saving…":"Save Settings"}</button>
          </div>
        </div>
      )}

      {/* Receipt Retention Tab */}
      {tab==="retention"&&(
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:4}}>🗄 Receipt Storage Retention</div>
          <div style={{fontSize:12,color:t.subText,marginBottom:20}}>Receipts are stored in Cloudflare R2. Set how long they are kept before automatic deletion.</div>
          {[
            {v:"6m",l:"6 Months",d:"Minimal storage. Good for frequent audits with short cycles."},
            {v:"1y",l:"1 Year",d:"Standard retention for most businesses."},
            {v:"2y",l:"2 Years",d:"Extended retention for compliance-heavy industries."},
            {v:"5y",l:"5 Years",d:"Long-term archival. Recommended for legal compliance."},
          ].map(opt=>(
            <div key={opt.v} style={{display:"flex",alignItems:"flex-start",gap:14,padding:"14px 16px",marginBottom:10,borderRadius:10,border:`2px solid ${cfg.receiptRetention===opt.v?"#22d3ee":t.border}`,background:cfg.receiptRetention===opt.v?"rgba(34,211,238,0.05)":t.inputBg,cursor:"pointer"}} onClick={()=>setCfg(c=>({...c,receiptRetention:opt.v}))}>
              <div style={{width:20,height:20,borderRadius:"50%",border:`2px solid ${cfg.receiptRetention===opt.v?"#22d3ee":t.border}`,background:cfg.receiptRetention===opt.v?"#22d3ee":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
                {cfg.receiptRetention===opt.v&&<div style={{width:8,height:8,borderRadius:"50%",background:"#0b0e17"}} />}
              </div>
              <div><div style={{fontSize:14,fontWeight:700,color:t.text}}>{opt.l}</div><div style={{fontSize:12,color:t.subText,marginTop:3}}>{opt.d}</div></div>
            </div>
          ))}
          <div style={{marginTop:16,padding:"12px 16px",background:"rgba(99,102,241,0.08)",borderRadius:8,fontSize:12,color:"#818cf8",border:"1px solid rgba(99,102,241,0.2)"}}>
            ℹ️ Note: Cloudflare R2 lifecycle rules must be configured separately in your Cloudflare dashboard to enforce automatic deletion. This setting documents your policy.
          </div>
          <div style={{marginTop:16,display:"flex",gap:12,alignItems:"center"}}>
            {saved&&<div style={{color:"#10b981",fontSize:13,background:"rgba(16,185,129,0.08)",padding:"8px 14px",borderRadius:8}}>✓ Saved!</div>}
            <button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={saveConfig}>{saving?"Saving…":"Save Settings"}</button>
          </div>
        </div>
      )}

      {/* WhatsApp/SMS Tab */}
      {tab==="whatsapp"&&(
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:4}}>💬 WhatsApp & SMS Notifications</div>
          <div style={{fontSize:12,color:t.subText,marginBottom:20}}>Send notifications via WhatsApp or SMS using Twilio. Requires a Twilio account.</div>
          <Toggle label="Enable WhatsApp Notifications" desc="Send WhatsApp messages via Twilio WhatsApp Business API" field="whatsappEnabled" />
          <Toggle label="Enable SMS Notifications" desc="Send SMS text messages via Twilio SMS" field="smsEnabled" />
          <Toggle label="Also send Email" desc="Send both email AND WhatsApp/SMS (if WhatsApp/SMS is enabled)" field="alsoSendEmail" />
          <div style={{marginTop:20,marginBottom:16,padding:"1px 0"}}>
            <div style={{fontSize:13,fontWeight:700,color:t.text,marginBottom:16,marginTop:16}}>Twilio Configuration</div>
            <div style={{marginBottom:14}}>
              <label style={S.label}>Twilio Account SID</label>
              <input style={S.input} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value={cfg.twilioSid||""} onChange={e=>setCfg(c=>({...c,twilioSid:e.target.value}))} />
            </div>
            <div style={{marginBottom:14}}>
              <label style={S.label}>Twilio Auth Token</label>
              <input style={S.input} type="password" placeholder="Your Twilio Auth Token" value={cfg.twilioToken||""} onChange={e=>setCfg(c=>({...c,twilioToken:e.target.value}))} />
            </div>
            <div style={{marginBottom:14}}>
              <label style={S.label}>From Phone Number (WhatsApp/SMS)</label>
              <input style={S.input} placeholder="+14155238886 (Twilio WhatsApp sandbox) or your Twilio number" value={cfg.twilioFrom||""} onChange={e=>setCfg(c=>({...c,twilioFrom:e.target.value}))} />
            </div>
          </div>
          <div style={{padding:"12px 16px",background:"rgba(245,158,11,0.08)",borderRadius:8,fontSize:12,color:"#f59e0b",border:"1px solid rgba(245,158,11,0.2)",marginBottom:16}}>
            ⚠️ To activate: Sign up at <strong>twilio.com</strong> (free trial available), get your Account SID and Auth Token, then add each user's phone number in the Users tab. The Supabase Edge Function will need updating to call the Twilio API.
          </div>
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            {saved&&<div style={{color:"#10b981",fontSize:13,background:"rgba(16,185,129,0.08)",padding:"8px 14px",borderRadius:8}}>✓ Saved!</div>}
            <button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={saveConfig}>{saving?"Saving…":"Save Settings"}</button>
          </div>
        </div>
      )}

      {/* Department Budgets Tab */}
      {tab==="budgets"&&(
        <div>
          <div style={{...S.card,marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:4}}>💰 Department Quarterly Budgets</div>
            <div style={{fontSize:12,color:t.subText,marginBottom:4}}>Set quarterly spend limits per department. Managers are alerted when 80% is reached.</div>
            <div style={{fontSize:11,color:"#22d3ee"}}>Current quarter: Q{Math.ceil((new Date().getMonth()+1)/3)} {new Date().getFullYear()}</div>
          </div>
          {COMPANIES.map(co=>(
            <div key={co.id} style={{...S.card,border:`1px solid ${co.color}25`}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}><CompanyTag companyId={co.id} size="lg" /></div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
                {DEPARTMENTS.map(dep=>(
                  <div key={dep}>
                    <label style={S.label}>{dep}</label>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:t.subText,fontSize:13}}>$</span>
                      <input style={{...S.input,flex:1}} type="number" placeholder="No limit" value={budgetForm[`${co.id}__${dep}`]||""} onChange={e=>setBudgetForm(f=>({...f,[`${co.id}__${dep}`]:e.target.value}))} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            {saved&&<div style={{color:"#10b981",fontSize:13,background:"rgba(16,185,129,0.08)",padding:"8px 14px",borderRadius:8}}>✓ Budgets saved!</div>}
            <button style={{...S.btn,...S.btnPrimary,opacity:saving?0.7:1}} onClick={saveBudgets}>{saving?"Saving…":"Save Budgets"}</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Archive Page ─────────────────────────────────────────────────────────────
function ArchivePage({expenses,trips,onUpdateExpenses,onUpdateTrips}) {
  const S=useS(); const t=S.t;
  const [tab,setTab]=useState("active");
  const [selected,setSelected]=useState(new Set());
  const [search,setSearch]=useState("");
  const [confirmDelete,setConfirmDelete]=useState(null);
  const [processing,setProcessing]=useState(false);

  const now=new Date();
  const thirtyDaysAgo=new Date(now-30*24*60*60*1000);

  const active=expenses.filter(e=>!e.archived&&!e.deletedAt);
  const archived=expenses.filter(e=>e.archived&&!e.deletedAt);
  const trashExp=expenses.filter(e=>e.deletedAt&&new Date(e.deletedAt)>thirtyDaysAgo);
  const trashTrips=trips.filter(tr=>tr.deletedAt&&new Date(tr.deletedAt)>thirtyDaysAgo);

  const getList=()=>{
    if(tab==="active") return active;
    if(tab==="archived") return archived;
    return trashExp;
  };
  const visible=getList().filter(e=>!search||`${e.description}${e.employeeName}${e.category}`.toLowerCase().includes(search.toLowerCase()));

  const toggleSelect=id=>setSelected(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n;});
  const toggleAll=()=>setSelected(s=>s.size===visible.length&&visible.length>0?new Set():new Set(visible.map(e=>e.id)));

  const archiveSelected=async()=>{
    if(selected.size===0)return; setProcessing(true);
    const ids=[...selected];
    await supabase.from("expenses").update({archived:true}).in("id",ids);
    onUpdateExpenses(expenses.map(e=>ids.includes(e.id)?{...e,archived:true}:e));
    setSelected(new Set()); setProcessing(false);
  };

  const unarchiveSelected=async()=>{
    if(selected.size===0)return; setProcessing(true);
    const ids=[...selected];
    await supabase.from("expenses").update({archived:false}).in("id",ids);
    onUpdateExpenses(expenses.map(e=>ids.includes(e.id)?{...e,archived:false}:e));
    setSelected(new Set()); setProcessing(false);
  };

  const softDeleteSelected=async()=>{
    if(selected.size===0)return; setProcessing(true);
    const ids=[...selected];
    const deletedAt=new Date().toISOString();
    await supabase.from("expenses").update({deleted_at:deletedAt,archived:false}).in("id",ids);
    onUpdateExpenses(expenses.map(e=>ids.includes(e.id)?{...e,deletedAt,archived:false}:e));
    setSelected(new Set()); setConfirmDelete(null); setProcessing(false);
  };

  const softDeleteTrip=async(tr)=>{
    const deletedAt=new Date().toISOString();
    await supabase.from("trips").update({deleted_at:deletedAt}).eq("id",tr.id);
    await supabase.from("expenses").update({deleted_at:deletedAt}).eq("trip_id",tr.id);
    onUpdateTrips(trips.map(x=>x.id===tr.id?{...x,deletedAt}:x));
    onUpdateExpenses(expenses.map(e=>e.tripId===tr.id?{...e,deletedAt}:e));
    setConfirmDelete(null);
  };

  const recoverExp=async(e)=>{
    await supabase.from("expenses").update({deleted_at:null}).eq("id",e.id);
    onUpdateExpenses(expenses.map(x=>x.id===e.id?{...x,deletedAt:null}:x));
  };

  const recoverTrip=async(tr)=>{
    await supabase.from("trips").update({deleted_at:null}).eq("id",tr.id);
    await supabase.from("expenses").update({deleted_at:null}).eq("trip_id",tr.id);
    onUpdateTrips(trips.map(x=>x.id===tr.id?{...x,deletedAt:null}:x));
    onUpdateExpenses(expenses.map(e=>e.tripId===tr.id?{...e,deletedAt:null}:e));
  };

  const permanentDelete=async()=>{
    setProcessing(true);
    let ids=[];
    if(confirmDelete==="selected") ids=[...selected];
    else if(confirmDelete==="all-archived") ids=archived.map(e=>e.id);
    else if(typeof confirmDelete==="string") ids=[confirmDelete];
    await supabase.from("expenses").delete().in("id",ids);
    onUpdateExpenses(expenses.filter(e=>!ids.includes(e.id)));
    setSelected(new Set()); setConfirmDelete(null); setProcessing(false);
  };

  const daysLeft=d=>Math.max(0,30-Math.floor((now-new Date(d))/(24*60*60*1000)));
  const totalUSD=visible.reduce((s,e)=>s+(e.amountUSD||0),0);

  return (
    <div>
      <h2 style={S.h2}>🗃 Archive & Trash</h2>
      <p style={S.sub}>Archive keeps expenses hidden but safe. Trash holds deleted items for 30 days before permanent removal.</p>

      <div style={{display:"flex",gap:8,marginBottom:20}}>
        <button style={{...S.btn,...(tab==="active"?S.btnPrimary:S.btnGhost),padding:"8px 20px"}} onClick={()=>{setTab("active");setSelected(new Set());}}>Active <span style={{marginLeft:6,opacity:.7}}>{active.length}</span></button>
        <button style={{...S.btn,...(tab==="archived"?S.btnPrimary:S.btnGhost),padding:"8px 20px"}} onClick={()=>{setTab("archived");setSelected(new Set());}}>🗃 Archived <span style={{marginLeft:6,opacity:.7}}>{archived.length}</span></button>
        <button style={{...S.btn,...(tab==="trash"?{background:"#ef4444",color:"#fff"}:S.btnGhost),padding:"8px 20px"}} onClick={()=>{setTab("trash");setSelected(new Set());}}>🗑 Trash <span style={{marginLeft:6,opacity:.7}}>{trashExp.length+trashTrips.length}</span></button>
      </div>

      {/* Toolbar */}
      <div style={{...S.card,padding:"14px 18px",marginBottom:16,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <input style={{...S.input,width:220,padding:"7px 14px"}} placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} />
        <span style={{fontSize:13,color:t.subText}}>{selected.size} selected</span>
        {tab==="active"&&<button style={{...S.btn,...S.btnWarn,opacity:(selected.size===0||processing)?0.5:1}} disabled={selected.size===0||processing} onClick={archiveSelected}>🗃 Archive Selected ({selected.size})</button>}
        {tab==="active"&&<button style={{...S.btn,...S.btnDanger,opacity:(selected.size===0||processing)?0.5:1}} disabled={selected.size===0||processing} onClick={()=>setConfirmDelete("selected")}>🗑 Move to Trash ({selected.size})</button>}
        {tab==="archived"&&<button style={{...S.btn,...S.btnSuccess,opacity:(selected.size===0||processing)?0.5:1}} disabled={selected.size===0||processing} onClick={unarchiveSelected}>↩ Restore Selected ({selected.size})</button>}
        {tab==="archived"&&<button style={{...S.btn,...S.btnDanger,opacity:(selected.size===0||processing)?0.5:1}} disabled={selected.size===0||processing} onClick={()=>setConfirmDelete("selected")}>🗑 Move to Trash ({selected.size})</button>}
        {tab==="archived"&&archived.length>0&&<button style={{...S.btn,...S.btnDanger,marginLeft:"auto"}} onClick={()=>setConfirmDelete("all-archived")}>🗑 Trash ALL Archived ({archived.length})</button>}
        <div style={{marginLeft:"auto",fontSize:13,color:t.subText}}>{visible.length} · <span style={{color:"#22d3ee",fontWeight:700}}>{fmtUSD(totalUSD)}</span></div>
      </div>

      {/* Trash info banner */}
      {tab==="trash"&&(
        <div style={{marginBottom:16,padding:"12px 16px",background:"rgba(239,68,68,0.08)",borderRadius:10,border:"1px solid rgba(239,68,68,0.25)",fontSize:13,color:"#ef4444"}}>
          🗑 Items in trash are automatically <strong>permanently deleted after 30 days</strong>. You can recover them before that.
        </div>
      )}

      {/* Expenses Table */}
      {tab!=="trash"&&(
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          <table style={S.table}>
            <thead>
              <tr style={{background:t.inputBg}}>
                <th style={{...S.th,width:40}}><input type="checkbox" checked={selected.size===visible.length&&visible.length>0} onChange={toggleAll} style={{cursor:"pointer"}} /></th>
                {["Company","Employee","Category","Description","USD","Date","Status","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {visible.length===0&&<tr><td colSpan={9} style={{...S.td,textAlign:"center",color:t.subText,padding:40}}>{tab==="active"?"No active expenses.":"No archived expenses."}</td></tr>}
              {visible.map(e=>{
                const isSelected=selected.has(e.id);
                return (
                  <tr key={e.id} style={{background:isSelected?"rgba(34,211,238,0.04)":undefined}}>
                    <td style={{...S.td,width:40}}><input type="checkbox" checked={isSelected} onChange={()=>toggleSelect(e.id)} style={{cursor:"pointer"}} /></td>
                    <td style={S.td}><CompanyTag companyId={e.company} /></td>
                    <td style={S.td}><span style={{fontWeight:600,color:t.text}}>{e.employeeName}</span></td>
                    <td style={S.td}><span style={{color:t.subText,fontSize:12}}>{e.category}</span></td>
                    <td style={S.td}><span style={{color:t.subText,maxWidth:160,display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.description}</span></td>
                    <td style={S.td}><span style={{fontWeight:700,color:"#22d3ee"}}>{fmtUSD(e.amountUSD)}</span></td>
                    <td style={S.td}><span style={{color:t.subText,fontSize:12}}>{e.date}</span></td>
                    <td style={S.td}><Badge status={e.status} /></td>
                    <td style={S.td}>
                      <div style={{display:"flex",gap:6}}>
                        {tab==="active"&&<button style={{...S.btn,...S.btnWarn,padding:"4px 10px",fontSize:11.5}} onClick={async()=>{await supabase.from("expenses").update({archived:true}).eq("id",e.id);onUpdateExpenses(expenses.map(x=>x.id===e.id?{...x,archived:true}:x));}}>🗃</button>}
                        {tab==="archived"&&<button style={{...S.btn,...S.btnSuccess,padding:"4px 10px",fontSize:11.5}} onClick={async()=>{await supabase.from("expenses").update({archived:false}).eq("id",e.id);onUpdateExpenses(expenses.map(x=>x.id===e.id?{...x,archived:false}:x));}}>↩</button>}
                        <button style={{...S.btn,...S.btnDanger,padding:"4px 10px",fontSize:11.5}} onClick={()=>setConfirmDelete(e.id)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Trash Tab */}
      {tab==="trash"&&(
        <div>
          {trashExp.length===0&&trashTrips.length===0&&<div style={{...S.card,textAlign:"center",color:t.subText,padding:40}}>Trash is empty.</div>}

          {/* Trashed Expenses */}
          {trashExp.length>0&&(
            <div style={S.card}>
              <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:16}}>🧾 Deleted Expenses ({trashExp.length})</div>
              {trashExp.map(e=>(
                <div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:`1px solid ${t.border}`}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:t.text}}>{e.description}</div>
                    <div style={{fontSize:12,color:t.subText,marginTop:2}}>{e.employeeName} · {e.category} · {fmtUSD(e.amountUSD)}</div>
                    <div style={{fontSize:11,color:"#ef4444",marginTop:2}}>⏳ {daysLeft(e.deletedAt)} days left to recover</div>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button style={{...S.btn,...S.btnSuccess,padding:"5px 12px",fontSize:12}} onClick={()=>recoverExp(e)}>↩ Recover</button>
                    <button style={{...S.btn,...S.btnDanger,padding:"5px 12px",fontSize:12}} onClick={()=>setConfirmDelete(e.id)}>🗑 Delete Forever</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Trashed Trips */}
          {trashTrips.length>0&&(
            <div style={S.card}>
              <div style={{fontSize:14,fontWeight:700,color:t.text,marginBottom:16}}>✈️ Deleted Trips ({trashTrips.length})</div>
              {trashTrips.map(tr=>(
                <div key={tr.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:`1px solid ${t.border}`}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:t.text}}>{tr.name}</div>
                    <div style={{fontSize:12,color:t.subText,marginTop:2}}>{tr.employeeName} · {tr.destination||"No destination"} · {tr.startDate}</div>
                    <div style={{fontSize:11,color:"#ef4444",marginTop:2}}>⏳ {daysLeft(tr.deletedAt)} days left to recover</div>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button style={{...S.btn,...S.btnSuccess,padding:"5px 12px",fontSize:12}} onClick={()=>recoverTrip(tr)}>↩ Recover Trip</button>
                    <button style={{...S.btn,...S.btnDanger,padding:"5px 12px",fontSize:12}} onClick={()=>setConfirmDelete(tr.id)}>🗑 Delete Forever</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirm Modal */}
      {confirmDelete&&(
        <div style={S.modal} onClick={e=>e.target===e.currentTarget&&setConfirmDelete(null)}>
          <div style={{...S.modalSm,maxWidth:480}}>
            <div style={{fontSize:28,textAlign:"center",marginBottom:12}}>⚠️</div>
            <div style={{fontSize:17,fontWeight:800,color:"#ef4444",textAlign:"center",marginBottom:12}}>
              {tab==="trash"?"Permanent Deletion Warning":"Move to Trash?"}
            </div>
            <div style={{fontSize:14,color:t.text,textAlign:"center",marginBottom:16,lineHeight:1.6}}>
              {tab==="trash"
                ?`This item will be permanently deleted and cannot be recovered.`
                :confirmDelete==="all-archived"
                  ?`Move ALL ${archived.length} archived expenses to trash?`
                  :confirmDelete==="selected"
                  ?`Move ${selected.size} selected expense${selected.size>1?"s":""} to trash?`
                  :"Move this expense to trash?"
              }
            </div>
            {tab==="trash"?(
              <div style={{padding:"14px 16px",background:"rgba(239,68,68,0.08)",borderRadius:10,border:"1px solid rgba(239,68,68,0.25)",marginBottom:20,fontSize:13,color:"#ef4444",lineHeight:1.7}}>
                🚨 This action <strong>cannot be undone</strong>. The item will be <strong>permanently removed</strong> from the system.
              </div>
            ):(
              <div style={{padding:"14px 16px",background:"rgba(245,158,11,0.08)",borderRadius:10,border:"1px solid rgba(245,158,11,0.25)",marginBottom:20,fontSize:13,color:"#f59e0b",lineHeight:1.7}}>
                ♻️ Items in trash can be <strong>recovered within 30 days</strong>. After that they are permanently deleted.
              </div>
            )}
            <div style={{display:"flex",gap:10}}>
              <button style={{...S.btn,...S.btnDanger,flex:1,padding:12,opacity:processing?0.7:1}} onClick={tab==="trash"?permanentDelete:softDeleteSelected} disabled={processing}>
                {processing?"Processing…":tab==="trash"?"Yes, Delete Forever":"Yes, Move to Trash"}
              </button>
              <button style={{...S.btn,...S.btnGhost,flex:1,padding:12}} onClick={()=>setConfirmDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const NAV={
  employee: [{id:"dash",icon:"⊞",label:"Dashboard"},{id:"analytics",icon:"📊",label:"Analytics"},{id:"submit",icon:"＋",label:"New Expense"},{id:"trip",icon:"✈",label:"New Trip"},{id:"list",icon:"≡",label:"My Expenses"},{id:"trips",icon:"🗂",label:"My Trips"},{id:"profile",icon:"◉",label:"My Profile"}],
  manager1: [{id:"dash",icon:"⊞",label:"Dashboard"},{id:"analytics",icon:"📊",label:"Analytics"},{id:"submit",icon:"＋",label:"New Expense"},{id:"trip",icon:"✈",label:"New Trip"},{id:"list",icon:"≡",label:"All Expenses"},{id:"trips",icon:"🗂",label:"All Trips"},{id:"profile",icon:"◉",label:"My Profile"}],
  manager2: [{id:"dash",icon:"⊞",label:"Dashboard"},{id:"analytics",icon:"📊",label:"Analytics"},{id:"submit",icon:"＋",label:"New Expense"},{id:"trip",icon:"✈",label:"New Trip"},{id:"list",icon:"≡",label:"All Expenses"},{id:"trips",icon:"🗂",label:"All Trips"},{id:"profile",icon:"◉",label:"My Profile"}],
  admin:    [{id:"dash",icon:"⊞",label:"Dashboard"},{id:"analytics",icon:"📊",label:"Analytics"},{id:"submit",icon:"＋",label:"New Expense"},{id:"trip",icon:"✈",label:"New Trip"},{id:"list",icon:"≡",label:"All Expenses"},{id:"trips",icon:"🗂",label:"All Trips"},{id:"users",icon:"👥",label:"Users"},{id:"settings",icon:"⚙️",label:"Approvers"},{id:"policy",icon:"💰",label:"Policies"},{id:"cats",icon:"🏷",label:"Categories"},{id:"archive",icon:"🗃",label:"Archive"},{id:"admin-cfg",icon:"🛠",label:"Admin Settings"},{id:"zoho",icon:"⬆",label:"Zoho Books"},{id:"profile",icon:"◉",label:"My Profile"}],
};

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [theme,setTheme] = useState(()=>localStorage.getItem("expenseio_theme")||"dark");
  const [sidebarOpen,setSidebarOpen]=useState(false);
  const toggleTheme=()=>{const n=theme==="dark"?"light":"dark";setTheme(n);localStorage.setItem("expenseio_theme",n);};

  const [user,setUser]           = useState(null);
  const [users,setUsers]         = useState([]);
  const [expenses,setExpenses]   = useState([]);
  const [trips,setTrips]         = useState([]);
  const [categories,setCategories] = useState([]);
  const [settings,setSettings]   = useState([]);
  const [policies,setPolicies]   = useState([]);
  const [appConfig,setAppConfig] = useState({
    notifyOnSubmit:true, notifyOnM1Approve:true, notifyOnM2Approve:true,
    notifyOnReject:true, notifyOnPaid:true, notifyOnBudgetAlert:true,
    approverCanSelfApprove:false, copyApproverOnFinalApproval:false,
    requireCommentOnReject:false, allowPartialResubmit:false,
    receiptRetention:"1y",
    whatsappEnabled:false, smsEnabled:false, alsoSendEmail:true,
    twilioSid:"", twilioToken:"", twilioFrom:"",
  });
  const [budgets,setBudgets]     = useState([]);
  const [page,setPage]           = useState("dash");
  const [selected,setSelected]   = useState(null);
  const [selectedTrip,setSelectedTrip] = useState(null);
  const [editTrip,setEditTrip]   = useState(null);
  const [zohoConfig,setZohoConfig] = useState({token:"",orgId:""});
  const [appLoading,setAppLoading] = useState(true);
  const {rates,toUSD,loading:ratesLoading,updated} = useRates();

  useEffect(()=>{
    const load=async()=>{
      try{
        // First try Supabase Auth session (most reliable)
        const {data:{session:authSession}}=await supabase.auth.getSession();
        if(authSession?.user){
          const {data:userData}=await supabase.from("users").select("*").eq("auth_id",authSession.user.id).single();
          if(userData&&userData.active){setUser(mapUser(userData));}
        } else {
          // Fallback: check localStorage session
          const saved=localStorage.getItem("expenseio_session");
          if(saved){
            const session=JSON.parse(saved);
            if(session.expiry>Date.now()){
              const {data:userData}=await supabase.from("users").select("*").eq("id",session.userId).single();
              if(userData&&userData.active){setUser(mapUser(userData));}
              else{localStorage.removeItem("expenseio_session");}
            } else {
              localStorage.removeItem("expenseio_session");
            }
          }
        }
      }catch(e){localStorage.removeItem("expenseio_session");}
      let {data:usersData}=await supabase.from("users").select("*");
      if(!usersData||usersData.length===0){await supabase.from("users").insert(SEED_USERS.map(userToDb));usersData=SEED_USERS;}
      else usersData=usersData.map(mapUser);
      setUsers(usersData);
      const {data:catData}=await supabase.from("categories").select("*").order("name");
      setCategories(catData||[]);
      const {data:expData}=await supabase.from("expenses").select("*").order("created_at",{ascending:false});
      setExpenses((expData||[]).map(mapExp));
      const {data:tripData}=await supabase.from("trips").select("*").order("created_at",{ascending:false});
      setTrips((tripData||[]).map(mapTrip));
      const {data:settingsData}=await supabase.from("settings").select("*");
      setSettings((settingsData||[]).map(mapSettings));
      const {data:polData,error:polErr}=await supabase.from("policies").select("*");
      setPolicies(polErr?[]:(polData||[]).map(p=>({company:p.company,category:p.category,maxUsd:Number(p.max_usd)})));
      const {data:cfgData}=await supabase.from("app_settings").select("*").eq("key","app_config").maybeSingle();
      if(cfgData?.value) setAppConfig(prev=>({...prev,...cfgData.value}));
      const {data:budgetData,error:budErr}=await supabase.from("department_budgets").select("*");
      setBudgets(budErr?[]:(budgetData||[]).map(b=>({company:b.company,department:b.department,quarter:b.quarter,year:b.year,budgetUsd:Number(b.budget_usd)})));
      setAppLoading(false);
    };
    load();
  },[]);

  const refreshAll=async()=>{
    const {data:e}=await supabase.from("expenses").select("*").order("created_at",{ascending:false});
    setExpenses((e||[]).map(mapExp));
    const {data:tr}=await supabase.from("trips").select("*").order("created_at",{ascending:false});
    setTrips((tr||[]).map(mapTrip));
  };

  const liveUser=user?users.find(u=>u.id===user.id)||user:null;
  const t=THEMES[theme];

  if(appLoading) return (
    <ThemeCtx.Provider value={theme}>
      <div style={{minHeight:"100vh",background:t.appBg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif"}}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:28,fontWeight:800,color:t.text,letterSpacing:"-1px",marginBottom:24}}>Expense<span style={{color:"#22d3ee"}}>.</span>io</div>
          <div style={{width:36,height:36,border:`3px solid ${t.border}`,borderTop:"3px solid #22d3ee",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto"}} />
          <div style={{color:t.subText,fontSize:13,marginTop:16}}>Connecting to database…</div>
        </div>
      </div>
    </ThemeCtx.Provider>
  );

  if(!liveUser&&!appLoading) return <ThemeCtx.Provider value={theme}><LoginPage onLogin={u=>{setUser(u);setPage("dash");refreshAll();}} /></ThemeCtx.Provider>;
  if(liveUser.mustChangePwd) return <ThemeCtx.Provider value={theme}><ForceChangePwd user={liveUser} onSaved={upd=>{setUsers(us=>us.map(u=>u.id===upd.id?upd:u));setUser(upd);}} /></ThemeCtx.Provider>;

  const nav=NAV[liveUser.role]||NAV.employee;
  const updateExpense=u=>setExpenses(es=>es.map(e=>e.id===u.id?u:e));
  const pushZoho=async e=>{
    try {
      const result=await supabase.functions.invoke("zoho-sync",{body:{expenses:[{
        id:e.id,
        company:e.company,
        date:e.date,
        amountUSD:e.amountUSD,
        amount:e.amount,
        currency:e.currency,
        description:e.description,
        vendor:e.vendor||"",
        category:e.category,
      }]}});
      const r=result.data?.results?.[0];
      if(r?.success){
        await supabase.from("expenses").update({pushed_to_zoho:true}).eq("id",e.id);
        updateExpense({...e,pushedToZoho:true});
      } else {
        alert("Zoho push failed: "+(r?.message||"Unknown error"));
      }
    } catch(err) {
      await supabase.from("expenses").update({pushed_to_zoho:true}).eq("id",e.id);
      updateExpense({...e,pushedToZoho:true});
    }
  };
  const updateUser=u=>{setUsers(us=>us.map(x=>x.id===u.id?u:x));if(u.id===liveUser.id)setUser(u);};
  const activeExpenses=expenses.filter(e=>!e.archived&&!e.deletedAt);
  const activeTrips=trips.filter(tr=>!tr.deletedAt);
  const activeCategories=categories.filter(c=>c.active).map(c=>c.name);
  const safeCats=activeCategories.length>0?activeCategories:["Food & Meals","Transport","Hotel","Flight","Subscription","Equipment","Training","Entertainment","Car Rental","Other"];

  const handleEditTrip=tr=>{setEditTrip(tr);setPage("trip");};

  const sendNotification=async(type,to,data)=>{
    console.log("Sending notification:", type, "to:", to);
    try{
      const result=await supabase.functions.invoke("send-notification",{body:{type,to,data}});
      console.log("Notification result:", result);
    }catch(e){console.log("Email error:",e);}
  };

  const handleSubmitTrip=async tr=>{
    // Check self-approval policy
    const setting=settings.find(s=>s.company===tr.company);
    const approver1=users.find(u=>u.id===setting?.approver1Id);
    if(!appConfig.approverCanSelfApprove && approver1?.id===tr.employeeId){
      alert("Policy: You cannot submit a trip where you are the approver.");
      return;
    }
    const updated={...tr,status:"pending",m1ApprovedBy:null,m2ApprovedBy:null,m1Comment:"",m2Comment:""};
    await supabase.from("trips").update({status:"pending",m1_approved_by:null,m2_approved_by:null,m1_comment:"",m2_comment:""}).eq("id",tr.id);
    await supabase.from("expenses").update({status:"pending",flagged:false,flag_note:null}).eq("trip_id",tr.id);
    setTrips(ts=>ts.map(x=>x.id===tr.id?updated:x));
    setExpenses(es=>es.map(e=>e.tripId===tr.id?{...e,status:"pending",flagged:false,flagNote:""}:e));
    const tripExps=expenses.filter(e=>e.tripId===tr.id);
    const totalUSD=tripExps.reduce((s,e)=>s+(e.amountUSD||0),0).toFixed(2);
    // Budget alert check
    if(appConfig.notifyOnBudgetAlert){
      const now=new Date();
      const quarter=`Q${Math.ceil((now.getMonth()+1)/3)}`;
      const year=now.getFullYear();
      const emp=users.find(u=>u.id===tr.employeeId);
      if(emp){
        const budget=budgets.find(b=>b.company===tr.company&&b.department===emp.department&&b.quarter===quarter&&b.year===year);
        if(budget){
          const deptExps=expenses.filter(e=>e.company===tr.company);
          const deptUsers=users.filter(u=>u.company===tr.company&&u.department===emp.department);
          const deptTotal=deptExps.filter(e=>deptUsers.find(u=>u.id===e.employeeId)&&new Date(e.date).getFullYear()===year&&Math.ceil((new Date(e.date).getMonth()+1)/3)===parseInt(quarter[1])).reduce((s,e)=>s+(e.amountUSD||0),0);
          if(deptTotal>budget.budgetUsd*0.8&&approver1?.email){
            sendNotification("trip_submitted",approver1.email,{
              tripName:`⚠️ Budget Alert: ${emp.department} dept at ${Math.round((deptTotal/budget.budgetUsd)*100)}% of $${budget.budgetUsd} budget`,
              employeeName:emp.name,expenseCount:tripExps.length,totalUSD,destination:"",
            });
          }
        }
      }
    }
    if(appConfig.notifyOnSubmit&&approver1?.email){
      sendNotification("trip_submitted",approver1.email,{
        tripName:tr.name,destination:tr.destination||"",
        employeeName:tr.employeeName,expenseCount:tripExps.length,totalUSD,
      });
    }
  };

  return (
    <ThemeCtx.Provider value={theme}>
      <div style={{fontFamily:"'DM Sans',sans-serif",display:"flex",height:"100vh",background:t.appBg,color:t.text,overflow:"hidden"}}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
          *{box-sizing:border-box}
          select option{background:${t.selectOpt};color:${t.text}}
          ::-webkit-scrollbar{width:6px;height:6px}
          ::-webkit-scrollbar-track{background:${t.scrollTrack}}
          ::-webkit-scrollbar-thumb{background:${t.scrollThumb};border-radius:3px}
          tr:hover td{background:${t.hoverBg}}
          @keyframes spin{to{transform:rotate(360deg)}}
          @media(max-width:768px){
            .sidebar-desktop{display:none!important}
            .hamburger{display:flex!important}
            .main-content{padding:16px!important}
            table{font-size:12px}
            th,td{padding:8px 10px!important}
          }
          @media(min-width:769px){
            .mobile-overlay{display:none!important}
            .sidebar-mobile{display:none!important}
            .hamburger{display:none!important}
          }
        `}</style>

        {/* Mobile overlay */}
        {sidebarOpen&&<div className="mobile-overlay" style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:40}} onClick={()=>setSidebarOpen(false)} />}

        {/* Mobile sidebar */}
        <div className="sidebar-mobile" style={{position:"fixed",top:0,left:sidebarOpen?0:-280,width:260,height:"100vh",background:t.sidebarBg,borderRight:`1px solid ${t.border}`,display:"flex",flexDirection:"column",padding:"24px 0",zIndex:50,transition:"left .25s ease",overflowY:"auto"}}>
          <div style={{padding:"0 24px 20px",borderBottom:`1px solid ${t.border}`,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:18,fontWeight:800,color:t.text}}>Expense<span style={{color:"#22d3ee"}}>.</span>io</div>
              <div style={{marginTop:6}}><CompanyTag companyId={liveUser.company} /></div>
            </div>
            <button style={{background:"none",border:"none",color:t.subText,fontSize:22,cursor:"pointer"}} onClick={()=>setSidebarOpen(false)}>✕</button>
          </div>
          {nav.map(n=><button key={n.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 24px",cursor:"pointer",fontSize:14,fontWeight:600,color:page===n.id?"#22d3ee":t.navColor,border:"none",background:page===n.id?"rgba(34,211,238,0.08)":"none",width:"100%",textAlign:"left",borderLeft:page===n.id?"3px solid #22d3ee":"3px solid transparent"}} onClick={()=>{setPage(n.id);if(n.id==="trip")setEditTrip(null);setSidebarOpen(false);}}><span>{n.icon}</span> {n.label}</button>)}
          <div style={{marginTop:"auto",padding:"16px 24px",borderTop:`1px solid ${t.border}`}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <Avatar name={liveUser.name} size={32} />
              <div><div style={{fontSize:13,fontWeight:600,color:t.text}}>{liveUser.name}</div><div style={{fontSize:11,color:t.subText,opacity:.7}}>{liveUser.role}</div></div>
            </div>
            <button style={{width:"100%",padding:"8px",borderRadius:8,border:"none",background:theme==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)",cursor:"pointer",fontSize:12,fontWeight:600,color:t.subText}} onClick={async()=>{await supabase.auth.signOut();localStorage.removeItem("expenseio_session");setUser(null);setPage("dash");}}>Sign out</button>
          </div>
        </div>

        {/* Desktop sidebar */}
        <div className="sidebar-desktop" style={{width:240,background:t.sidebarBg,borderRight:`1px solid ${t.border}`,display:"flex",flexDirection:"column",padding:"24px 0",flexShrink:0}}>
          <div style={{padding:"0 24px 28px",borderBottom:`1px solid ${t.border}`,marginBottom:8}}>
            <div style={{fontSize:18,fontWeight:800,color:t.text,letterSpacing:"-0.5px"}}>Expense<span style={{color:"#22d3ee"}}>.</span>io</div>
            <div style={{marginTop:8}}><CompanyTag companyId={liveUser.company} /></div>
          </div>
          {nav.map(n=><button key={n.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 24px",cursor:"pointer",fontSize:13.5,fontWeight:theme==="light"?700:500,color:page===n.id?"#22d3ee":t.navColor,border:"none",background:page===n.id?"rgba(34,211,238,0.08)":"none",width:"100%",textAlign:"left",transition:"all .15s",borderLeft:page===n.id?"3px solid #22d3ee":"3px solid transparent"}} onClick={()=>{setPage(n.id);if(n.id==="trip")setEditTrip(null);}}><span>{n.icon}</span> {n.label}</button>)}
          <div style={{marginTop:"auto",padding:"16px 24px",borderTop:`1px solid ${t.border}`}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <Avatar name={liveUser.name} size={32} />
              <div><div style={{fontSize:13,fontWeight:600,color:t.text}}>{liveUser.name}</div><div style={{fontSize:11,color:t.subText,opacity:.7}}>{liveUser.department} · {liveUser.role}</div></div>
            </div>
            <button style={{display:"flex",alignItems:"center",justifyContent:"center",width:"100%",padding:"7px 12px",borderRadius:8,border:"none",background:theme==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)",cursor:"pointer",fontSize:12,fontWeight:600,color:t.subText}} onClick={async()=>{await supabase.auth.signOut();localStorage.removeItem("expenseio_session");setUser(null);setPage("dash");}}>Sign out</button>
          </div>
        </div>

        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <div style={{height:60,background:t.topbar,borderBottom:`1px solid ${t.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px",flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <button className="hamburger" style={{display:"none",background:"none",border:"none",cursor:"pointer",padding:4,color:t.text,fontSize:22}} onClick={()=>setSidebarOpen(true)}>☰</button>
              <div style={{fontSize:13,color:t.subText}}>{nav.find(n=>n.id===page)?.label}</div>
            </div>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              {ratesLoading&&<span style={{fontSize:12,color:"#f59e0b"}}>⟳ Loading rates…</span>}
              <span style={{fontSize:11,background:"rgba(34,211,238,0.08)",color:"#22d3ee",padding:"3px 10px",borderRadius:20,fontWeight:700}}>🗄 Supabase · USD 🇺🇸</span>
              <button style={{padding:"5px 12px",borderRadius:8,border:`1px solid ${t.border}`,background:"none",cursor:"pointer",fontSize:12,fontWeight:600,color:t.subText}} onClick={toggleTheme}>{theme==="dark"?"☀ Light":"🌙 Dark"}</button>
            </div>
          </div>
          <RatesTicker rates={rates} updated={updated} />
          <div className="main-content" style={{flex:1,overflow:"auto",padding:28,WebkitOverflowScrolling:"touch"}}>
            {page==="dash"     && <Dashboard       user={liveUser} expenses={activeExpenses} trips={activeTrips} users={users} />}
            {page==="analytics"&& <AnalyticsPage   user={liveUser} expenses={activeExpenses} trips={activeTrips} users={users} />}
            {page==="submit"   && <SubmitExpense   user={liveUser} toUSD={toUSD} rates={rates} categories={safeCats} trips={activeTrips} policies={policies} onGoToTrip={()=>setPage("trip")} onSubmit={e=>{setExpenses(es=>[e,...es]);}} />}
            {page==="trip"     && <TripCreate      user={liveUser} editTrip={editTrip} onSave={tr=>{if(editTrip){setTrips(ts=>ts.map(x=>x.id===tr.id?tr:x));}else{setTrips(ts=>[tr,...ts]);}setEditTrip(null);setPage("trips");}} />}
            {page==="list"     && <ExpenseList     user={liveUser} expenses={activeExpenses} onSelect={setSelected} />}
            {page==="trips"    && <TripList        user={liveUser} trips={activeTrips} expenses={activeExpenses} onSelect={tr=>setSelectedTrip(tr)} onEdit={handleEditTrip} onSubmitTrip={handleSubmitTrip} onDeleteTrip={id=>setTrips(ts=>ts.filter(tr=>tr.id!==id))} onDeleteExp={id=>setExpenses(es=>es.filter(e=>e.id!==id))} />}
            {page==="users"    && <UserManagement  users={users} onUpdateUsers={setUsers} />}
            {page==="settings" && <ApproverSettings users={users} settings={settings} onUpdateSettings={setSettings} />}
            {page==="policy"   && <SpendPolicyPage  categories={safeCats} policies={policies} onUpdatePolicies={setPolicies} />}
            {page==="cats"     && <CategoryManagement categories={categories} onUpdateCategories={setCategories} />}
            {page==="archive"  && <ArchivePage      expenses={expenses} trips={trips} onUpdateExpenses={setExpenses} onUpdateTrips={setTrips} />}
            {page==="admin-cfg"&& <AdminSettingsPage appConfig={appConfig} onSaveConfig={setAppConfig} users={users} budgets={budgets} onSaveBudgets={setBudgets} />}
            {page==="zoho"     && <ZohoPanel        expenses={activeExpenses} onPushZoho={pushZoho} />}
            {page==="profile"  && <ProfilePage      user={liveUser} onUpdateUser={updateUser} />}
          </div>
        </div>

        {selected&&<ExpenseDetail exp={expenses.find(e=>e.id===selected.id)||selected} user={liveUser} users={users} settings={settings} onClose={()=>setSelected(null)} onUpdate={updateExpense} onPushZoho={pushZoho} />}
        {selectedTrip&&<TripDetail trip={trips.find(tr=>tr.id===selectedTrip.id)||selectedTrip} expenses={expenses} users={users} user={liveUser} settings={settings} appConfig={appConfig} onClose={()=>setSelectedTrip(null)} onUpdate={tr=>setTrips(ts=>ts.map(x=>x.id===tr.id?tr:x))} onUpdateExp={updateExpense} />}
      </div>
    </ThemeCtx.Provider>
  );
}
